import React, { useState, useEffect, useRef } from 'react';
import Editor from "@monaco-editor/react";
import API_BASE_URL from"../Config/config";
import useRandomCapture from '../hooks/useRandomCapture';

import {
  Container,
  Box,
  Grid,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Avatar,
  Fade,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Snackbar,
  Chip
} from '@mui/material';
import {
  Mic,
  MicOff,
  Videocam,
  VideocamOff,
  Stop,
  PlayArrow,
  SmartToy,
  AssignmentOutlined,
  PersonOutline,
  VideocamOutlined,
  MicNoneOutlined,
  VisibilityOutlined,
  PhonelinkOffOutlined,
  VpnLockOutlined,
  DoDisturbOnOutlined,
  InfoOutlined,
  CenterFocusStrongOutlined,
  RemoveRedEyeOutlined,
  ScreenRotationOutlined,
  TabUnselectedOutlined,
  GroupsOutlined,
  SecurityOutlined,
  WarningAmberOutlined,
  Check,
  Lock
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as faceapi from "face-api.js";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import ViolationModal from '../components/ViolationModal';
import ArchitectureCanvas from '../components/ArchitectureCanvas';
import { buildSQLSetupScript, SQL_LANGUAGE_ID } from '../utils/sqlSchemaBuilder';

// ─── Architecture eligibility ─────────────────────────────────────────────────
const ARCH_SKILLS = new Set([
  'java','python','python backend','spring boot','node.js','nodejs','django','fastapi',
  '.net','golang','go','rest api','distributed systems','kafka','redis','system design',
  'database design','backend architecture','microservices','aws','azure','gcp','docker',
  'kubernetes','devops','terraform','ci/cd','cicd','spark','hadoop','etl','data engineering',
  'machine learning','deep learning','genai','llm','mlops','react','angular','vue',
  'cybersecurity','network security',
]);
const ARCH_QUESTION_MAP = {
  'aws':                 'Design a scalable, highly available AWS architecture for an e-commerce platform handling 1M daily users.',
  'azure':               'Design an Azure cloud architecture for a multi-tenant enterprise SaaS application.',
  'gcp':                 'Design a Google Cloud data pipeline architecture for real-time analytics at scale.',
  'kubernetes':          'Design a Kubernetes-based microservices deployment architecture with auto-scaling and observability.',
  'docker':              'Design a containerized application architecture with Docker, covering build, deploy, and networking.',
  'devops':              'Design a complete CI/CD pipeline architecture for a microservices application with automated testing and deployment.',
  'ci/cd':               'Design a full CI/CD pipeline architecture with blue-green deployments and rollback strategy.',
  'cicd':                'Design a full CI/CD pipeline architecture with blue-green deployments and rollback strategy.',
  'kafka':               'Design an event-driven architecture using Apache Kafka for a financial transaction processing system.',
  'microservices':       'Design a scalable microservices architecture with service mesh, API gateway, and distributed tracing.',
  'system design':       'Design a scalable distributed system architecture for a social media platform supporting 10M users.',
  'distributed systems': 'Design a fault-tolerant distributed architecture with consistent hashing and leader election.',
  'machine learning':    'Design an end-to-end ML training and inference pipeline architecture for production.',
  'mlops':               'Design a production MLOps architecture covering training, versioning, deployment, and monitoring.',
  'genai':               'Design a Retrieval-Augmented Generation (RAG) architecture with vector store and LLM integration.',
  'llm':                 'Design an LLM-based application architecture with prompt caching, rate limiting, and fallback handling.',
  'deep learning':       'Design a deep learning model training and serving architecture with GPU clusters.',
  'spark':               'Design a real-time data processing architecture using Apache Spark Streaming and Delta Lake.',
  'data engineering':    'Design a modern data lakehouse architecture with ingestion, transformation, and serving layers.',
  'etl':                 'Design a scalable ETL pipeline architecture with error handling, monitoring, and idempotency.',
  'react':               'Design a scalable React frontend architecture with micro-frontends, state management, and code splitting.',
  'angular':             'Design an Angular enterprise frontend architecture with lazy loading and module federation.',
  'cybersecurity':       'Design a zero-trust network security architecture for a cloud-native application.',
  'spring boot':         'Design a Spring Boot microservices architecture with service discovery, config server, and circuit breaker.',
  'node.js':             'Design a high-throughput Node.js backend architecture with event-driven patterns and horizontal scaling.',
  'nodejs':              'Design a high-throughput Node.js backend architecture with event-driven patterns and horizontal scaling.',
};

function getArchQuestion(primarySkills, level) {
  if (!level || level.toLowerCase() !== 'experienced') return null;
  const skills = (typeof primarySkills === 'string' ? primarySkills.split(',') : (primarySkills || []))
    .map(s => s.trim().toLowerCase());
  for (const skill of skills) {
    if (ARCH_QUESTION_MAP[skill]) return ARCH_QUESTION_MAP[skill];
    if (ARCH_SKILLS.has(skill)) return `Design a scalable ${skill} architecture for a production system, covering key components, data flow, scaling strategy, and fault tolerance.`;
  }
  return null;
}


// Console interceptor injected into the frontend preview iframe.
// Uses '<' + '/script>' split to prevent the HTML parser from closing the tag prematurely.
const CONSOLE_CAPTURE_SCRIPT = '<script>(function(){["log","warn","error","info"].forEach(function(t){var o=console[t].bind(console);console[t]=function(){var m=Array.from(arguments).map(function(a){return typeof a==="object"?JSON.stringify(a):String(a)}).join(" ");window.parent.postMessage({__exihire:true,level:t,message:m},"*");o.apply(console,arguments)};});window.onerror=function(m,s,l){window.parent.postMessage({__exihire:true,level:"error",message:m+" ("+s+":"+l+")"},"*")};})();<' + '/script>';

function AIInterview() {
  const { interviewId, sessionToken } = useParams();
  // sessionToken is set when accessed via /candidate/ai-technical-interview/:sessionToken
  //                                     or /candidate/ai-hr-interview/:sessionToken
  // interviewId is set when accessed via /candidate/ai-interview/:interviewId (assessment flow)
  const isTechInterviewSession = !!sessionToken;
  const isHrInterviewSession = window.location.pathname.startsWith('/candidate/ai-hr-interview/');
  // effectiveId is the applicationId used for proctoring/random-capture; resolved from context response for token-based flow
  const [applicationId, setApplicationId] = React.useState(isTechInterviewSession ? null : interviewId);
  console.log("INTERVIEW ID:", interviewId, "SESSION TOKEN:", sessionToken, "IS_TECH_SESSION:", isTechInterviewSession, "IS_HR_SESSION:", isHrInterviewSession);
  const navigate = useNavigate();

  // ---- Device/session security client hints (non-sensitive) ----
  // Used by backend middleware to detect device change mid-session.
  const getScreenResolution = () => {
    try {
      const w = window?.screen?.width;
      const h = window?.screen?.height;
      if (!w || !h) return null;
      return `${w}x${h}`;
    } catch {
      return null;
    }
  };

  const getDeviceHeaders = () => {
    const sr = getScreenResolution();
    return sr ? { "X-Screen-Resolution": sr } : {};
  };

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [context, setContext] = useState(null);
  const [stage, setStage] = useState('setup'); // setup, intro, questioning, wrapup, finished

  const [isCameraOn, setIsCameraOn] = useState(false);
  const [isMicOn, setIsMicOn] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isAISpeaking, setIsAISpeaking] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);


  const [code, setCode] = useState("");
  const [language, setLanguage] = useState("");
  const [lockedCompiler, setLockedCompiler] = useState(null);
  const [htmlCode, setHtmlCode] = useState("<!-- Write your HTML here -->\n<h1>Hello World</h1>");
  const [cssCode, setCssCode] = useState("/* Write your CSS here */\nbody {\n  font-family: sans-serif;\n  padding: 20px;\n}");
  const [frontendTab, setFrontendTab] = useState("html");
  const [schemaExpanded, setSchemaExpanded] = useState(false);
  const [terminalOutput, setTerminalOutput] = useState("ExiHire Code Execution Environment Ready\nSelect a language and run your code.");
  const [sqlQueryResult, setSqlQueryResult] = useState(null); // { headers: string[], rows: string[][] }
  const [sqlHiddenTestResults, setSqlHiddenTestResults] = useState([]); // hidden dataset results
  const [sqlTierScore, setSqlTierScore] = useState(null);    // 0/30/60/80/100
  const [sqlFeedback, setSqlFeedback] = useState([]);        // specific feedback messages
  const [isExecuting, setIsExecuting] = useState(false);
  const [testCaseSummary, setTestCaseSummary] = useState(null);
  const [executionStats, setExecutionStats] = useState({ time: null, memory: null });
  const [stdin, setStdin] = useState("");
  const [showStdin, setShowStdin] = useState(false);
  const [terminalHeight, setTerminalHeight] = useState(200);
  const [compilerPanelHeight, setCompilerPanelHeight] = useState(400);
  const [archCanvasData, setArchCanvasData] = useState(null);
  const [isCameraInitializing, setIsCameraInitializing] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);

  const [transcript, setTranscript] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(-1);
  const [interviewStarted, setInterviewStarted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null); // in seconds
  const [elapsedTime, setElapsedTime] = useState(0);
  const timeLeftRef = useRef(timeLeft);
  const [answers, setAnswers] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [showNextButton, setShowNextButton] = useState(false);
  const [loadingNext, setLoadingNext] = useState(false);
  const [tabSwitchCount, setTabSwitchCount] = useState(0);
  const [isTerminated, setIsTerminated] = useState(false);
  const getInterviewTitle = () => {
    if (context?.interviewType === 'TECHNICAL_EVALUATION') return 'Technical Evaluation Round';
    if (context?.interviewType === 'AI_TECHNICAL') return 'AI Technical Interview';
    if (context?.interviewType === 'HR_BEHAVIORAL') return 'HR & Behavioral Evaluation';
    return 'AI Interview Assessment';
  };

  const getInterviewerName = () => {
    if (context?.interviewType === 'TECHNICAL_EVALUATION') return 'Technical Architect';
    if (context?.interviewType === 'AI_TECHNICAL') return 'Technical Interviewer';
    if (context?.interviewType === 'HR_BEHAVIORAL') return 'HR Manager';
    return 'AI Interviewer';
  };

  const SUPPORTED_LANGUAGES = [
    { label: "Python", value: "python", monaco: "python", judge0: 71 }, // Python 3
    { label: "Java", value: "java", monaco: "java", judge0: 62 },
    { label: "C", value: "c", monaco: "c", judge0: 50 },
    { label: "C++", value: "cpp", monaco: "cpp", judge0: 54 },
    { label: "JavaScript (Node.js)", value: "javascript", monaco: "javascript", judge0: 63 },
    { label: "SQL", value: "sql", monaco: "sql", judge0: 82 },
    { label: "C#", value: "csharp", monaco: "csharp", judge0: 51 },
    { label: "Go", value: "go", monaco: "go", judge0: 60 },
    { label: "PHP", value: "php", monaco: "php", judge0: 68 },
    { label: "Ruby", value: "ruby", monaco: "ruby", judge0: 72 },
    { label: "Swift", value: "swift", monaco: "swift", judge0: 83 },
    { label: "Kotlin", value: "kotlin", monaco: "kotlin", judge0: 78 },
    { label: "Rust", value: "rust", monaco: "rust", judge0: 73 },
    { label: "TypeScript", value: "typescript", monaco: "typescript", judge0: 74 },
  ];

  const handleLanguageChange = (val) => {
    setLanguage(val);
    if (!val) {
      setTerminalOutput("ExiHire Code Execution Environment Ready\nSelect a language and run your code.");
      setCode("");
    } else {
      setTerminalOutput(`ExiHire Code Execution Environment Ready\nLanguage: ${val}\n---`);
      // Optional: Set some default code boilerplate
      if (val === "python") setCode("# Write your code here\nprint('Hello World')");
      else if (val === "java") setCode("public class Main {\n    public static void main(String[] args) {\n        System.out.println(\"Hello World\");\n    }\n}");
      else if (val === "c") setCode("#include <stdio.h>\n\nint main() {\n    printf(\"Hello World\\n\");\n    return 0;\n}");
      else if (val === "cpp") setCode("#include <iostream>\n\nint main() {\n    std::cout << \"Hello World\" << std::endl;\n    return 0;\n}");
      else if (val === "javascript") setCode("// Write your code here\nconsole.log('Hello World');");
      else if (val === "sql") setCode("-- Write your SQL query here\nSELECT * FROM table_name\nWHERE condition;");
      else if (val === "frontend") setCode("<!DOCTYPE html>\n<html>\n<head>\n  <title>Preview</title>\n</head>\n<body>\n  <!-- Write your HTML/CSS/JS here -->\n</body>\n</html>");
      else setCode("");
    }
  };

  const parseSQLQuestion = (text) => {
    if (!text) return null;
    const clean = text.replace(/\*\*/g, '').replace(/\*/g, '');
    // Normalise literal \n escape sequences that survived JSON serialisation
    const normalised = clean.replace(/\\n/g, '\n');
    const lines = normalised.split('\n').map(l => l.trim()).filter(Boolean);
    const buckets = { schema: [], sample: [], task: [], constraints: [], output: [] };
    let cur = 'task';
    for (const line of lines) {
      // Standalone section headers (line is ONLY the header)
      const lineLower = line.toLowerCase();
      if (/^(?:schema|database schema|tables?|given the (?:following )?tables?)[\s:]*$/i.test(lineLower)) { cur = 'schema'; continue; }
      if (/^(?:sample data|data|test data)[\s:]*$/i.test(lineLower)) { cur = 'sample'; continue; }
      if (/^(?:task|problem(?: statement)?|your task|problem)[\s:]*$/i.test(lineLower)) { cur = 'task'; continue; }
      if (/^(?:constraints?|requirements?|notes?|constraints?\s*\/\s*notes?)[\s:]*$/i.test(lineLower)) { cur = 'constraints'; continue; }
      if (/^(?:expected output|sample output|output format|expected result)[\s:]*$/i.test(lineLower)) { cur = 'output'; continue; }
      
      // Inline "Header: value" patterns
      if (/^(?:table schema|schema)\s*:/i.test(line))   { cur = 'schema'; const rest = line.replace(/^(?:table schema|schema)\s*:\s*/i, ''); if (rest) buckets.schema.push(rest); continue; }
      if (/^(?:sample data?|data)\s*:/i.test(line))     { cur = 'sample'; const rest = line.replace(/^(?:sample data?|data)\s*:\s*/i, ''); if (rest) buckets.sample.push(rest); continue; }
      if (/^task\s*:/i.test(line))                      { cur = 'task';   const rest = line.replace(/^task\s*:\s*/i, ''); if (rest) buckets.task.push(rest); continue; }
      if (/^task hint\s*:/i.test(line))                 { cur = 'task';   const rest = line.replace(/^task hint\s*:\s*/i, ''); if (rest) buckets.task.push(rest); continue; }
      if (/^constraints?\s*:/i.test(line))              { cur = 'constraints'; const rest = line.replace(/^constraints?\s*:\s*/i, ''); if (rest) buckets.constraints.push(rest); continue; }
      if (/^(?:expected output|output|expected result)\s*:/i.test(line)){ cur = 'output'; const rest = line.replace(/^(?:expected output|output|expected result)\s*:\s*/i, ''); if (rest) buckets.output.push(rest); continue; }
      buckets[cur].push(line);
    }
    return {
      schema:         buckets.schema.length  ? buckets.schema.join('\n')  : null,
      sampleData:     buckets.sample.length  ? buckets.sample.join('\n')  : null,
      task:           buckets.task.filter(l => l.length > 0),
      constraints:    buckets.constraints.filter(l => l.length > 0),
      expectedOutput: buckets.output.length  ? buckets.output.join('\n')  : null,
    };
  };

  const renderSchemaLine = (line, idx) => {
    const hasPK = /\b(?:primary[_ ]?key|pk)\b/i.test(line);
    const hasFK = /\b(?:foreign[_ ]?key|fk|references)\b/i.test(line);
    const isTableHeader = /^\w+\s*\(/.test(line.trim()) || /^CREATE\s+TABLE/i.test(line.trim());
    return (
      <Box key={idx} sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 0.25, flexWrap: 'wrap' }}>
        <Typography component="span" sx={{
          fontFamily: '"Courier New", monospace',
          fontSize: '11px',
          color: isTableHeader ? '#e6db74' : '#c9d1d9',
          fontWeight: isTableHeader ? 700 : 400,
          wordBreak: 'break-word',
          flex: 1,
        }}>
          {line}
        </Typography>
        {hasPK && <Chip label="PK" sx={{ ml: 0.25, bgcolor: '#3d2000', color: '#f0883e', border: '1px solid #f0883e55', fontSize: '9px', height: 15, '& .MuiChip-label': { px: 0.5, lineHeight: 1 } }} />}
        {hasFK && <Chip label="FK" sx={{ ml: 0.25, bgcolor: '#001a3d', color: '#58a6ff', border: '1px solid #58a6ff55', fontSize: '9px', height: 15, '& .MuiChip-label': { px: 0.5, lineHeight: 1 } }} />}
      </Box>
    );
  };

  const [lastExecutionMetadata, setLastExecutionMetadata] = useState(null);
  const [testCaseResults, setTestCaseResults] = useState([]);
  const [consoleLogs, setConsoleLogs] = useState([]);

  // Auto-select and lock compiler when question has a compiler field
  useEffect(() => {
    if (currentQuestionIndex < 0 || !questions[currentQuestionIndex]) return;
    const q = questions[currentQuestionIndex];

    // Reset stale execution metadata from the previous question
    setLastExecutionMetadata(null);
    setTestCaseResults([]);
    setTestCaseSummary(null);
    setExecutionStats({ time: null, memory: null });
    setConsoleLogs([]);
    setSqlQueryResult(null);
    setSqlHiddenTestResults([]);
    setSqlTierScore(null);
    setSqlFeedback([]);
    setTerminalOutput("ExiHire Code Execution Environment Ready\nSelect a language and run your code.");
    // Issue 1: reset stdin so stale input from the previous question never bleeds over
    setStdin("");
    setShowStdin(false);

    if (q.compiler) {
      setLockedCompiler(q.compiler);
      setLanguage(q.compiler);
      setSchemaExpanded(false);
      if (q.compiler === "frontend") {
        setHtmlCode("<!-- Write your HTML here -->\n<h1>Hello World</h1>");
        setCssCode("/* Write your CSS here */\nbody {\n  font-family: sans-serif;\n  padding: 20px;\n}");
        setFrontendTab("html");
      } else {
        handleLanguageChange(q.compiler);
      }
    } else {
      setLockedCompiler(null);
      setSchemaExpanded(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestionIndex, questions.length]);

  const runCode = async () => {
    if (!language || !SUPPORTED_LANGUAGES.find(l => l.value === language)) return;
    setIsExecuting(true);
    setTerminalOutput("Running...");
    setTestCaseResults([]);
    setTestCaseSummary(null);
    setExecutionStats({ time: null, memory: null });
    setSqlQueryResult(null);

    try {
      const selectedLang = SUPPORTED_LANGUAGES.find(l => l.value === language);
      const currentQ     = questions[currentQuestionIndex];
      const isSQLRun     = selectedLang.judge0 === SQL_LANGUAGE_ID;

      console.log("Current Question:", currentQ);
      console.log("Visible Test Cases:", currentQ?.visibleTestCases);

      const visibleTests = currentQ?.visibleTestCases || currentQ?.testCases || [];
      console.log("Sending Test Cases:", visibleTests);

      if (!isSQLRun && currentQ?.type === 'coding' && (!visibleTests || visibleTests.length === 0)) {
        console.error("No test cases found for coding question");
        setTerminalOutput("Error: This coding question has no test cases configured. Please contact the recruiter.");
        setIsExecuting(false);
        return;
      }

      // For SQL: build the table-setup script from question schema/data.
      // The backend prepends it before running so the candidate never sees
      // "no such table" errors. source_code stays as the candidate's query only.
      let sqlSetupScript = '';
      if (isSQLRun) {
        sqlSetupScript = buildSQLSetupScript(currentQ, aiResponse);
        if (sqlSetupScript) {
          setTerminalOutput("Loading SQL environment...");
        }
      }

      const res = await axios.post(`${API_BASE_URL}/interviewRoutes/execute-code`, {
        source_code:          code,
        language_id:          selectedLang.judge0,
        stdin:                isSQLRun ? null : stdin, // SQL does not use stdin
        sqlSetupScript:       sqlSetupScript || undefined,
        applicationId:        interviewId,
        languageName:         selectedLang.label,
        testCases:            visibleTests,
        minimumPassThreshold: currentQ?.minimumPassThreshold || 2,
        // SQL hidden test cases for anti-cheating validation (not shown to candidate)
        hiddenTestCases:      (isSQLRun && currentQ?.hiddenTestCases?.length > 0)
                                ? currentQ.hiddenTestCases
                                : undefined,
      });

      const {
        stdout, stderr, compile_output, message, status,
        testCaseResults: tcResults,
        testCaseSummary: tcSummary, time, memory,
        sqlHiddenTestResults: hiddenResults,
        sqlTierScore: tierScore,
        sqlFeedback: feedbackMsgs,
      } = res.data;

      setTestCaseSummary(tcSummary);
      setExecutionStats({ time, memory });
      if (isSQLRun) {
        setSqlHiddenTestResults(hiddenResults || []);
        setSqlTierScore(tierScore !== undefined ? tierScore : null);
        setSqlFeedback(feedbackMsgs || []);
      }

      // ── Debug logging (visible in browser console) ───────────────────────────
      if (isSQLRun) {
        console.log('[SQL Debug] Setup script:\n', sqlSetupScript || '(none — no schema detected in question)');
        console.log('[SQL Debug] Raw stdout:', stdout ? atob(stdout) : '(empty)');
        console.log('[SQL Debug] Raw stderr:', stderr ? atob(stderr) : '(none)');
        console.log('[SQL Debug] Status:', status?.id, status?.description);
      }

      let finalOutput = "";
      if (compile_output) finalOutput += `Compile Output:\n${atob(compile_output)}\n`;

      // Format test case results for the terminal
      if (tcResults && tcResults.length > 0) {
        finalOutput += "\n---\n\nTEST CASE RESULTS\n\n---\n";
        tcResults.forEach((tc, idx) => {
          finalOutput += `Test Case ${idx + 1}\n\n`;
          finalOutput += `Input:\n${tc.input || "(no input)"}\n\n`;
          finalOutput += `Expected:\n${tc.expectedOutput}\n\n`;
          finalOutput += `Actual:\n${tc.actualOutput || "(no output)"}\n\n`;
          finalOutput += `Result:\n${tc.passed ? "PASSED" : "FAILED"}\n`;
          finalOutput += "\n---\n";
        });

        if (tcSummary) {
          finalOutput += `Overall:\n\n`;
          finalOutput += `Passed: ${tcSummary.passed} / ${tcSummary.total}\n\n`;
          finalOutput += `Accuracy: ${tcSummary.accuracy}%\n\n`;
          finalOutput += "---\n";
        }
      }

      if (isSQLRun) {
        // ── SQL result path ────────────────────────────────────────────────────
        const rawStdout   = stdout ? atob(stdout).trim() : '';
        const stderrText  = stderr ? atob(stderr).trim() : '';

        if (stderrText) {
          // SQLite error (syntax error, missing table, etc.)
          finalOutput += `Error:\n${stderrText}\n`;
          setSqlQueryResult(null);
        } else if (rawStdout) {
          // Parse pipe-delimited output: first line = headers (from .headers on), rest = data
          const lines = rawStdout.split('\n').filter(l => l.trim() !== '');
          const headers  = lines[0].split('|');
          const dataRows = lines.slice(1).map(l => l.split('|'));
          setSqlQueryResult({ headers, rows: dataRows });
          
          if (dataRows.length > 0) {
            finalOutput = `✓ Query executed successfully — ${dataRows.length} row(s) returned`;
          } else {
            finalOutput = `✓ Query executed successfully — 0 rows returned`;
          }
          if (sqlSetupScript) finalOutput += ' (schema auto-loaded)';
        } else {
          // No stdout and no stderr → query ran OK but returned 0 rows
          // If .headers on is working, we should at least have a header line if table exists.
          // If even that's missing, it might be a DDL/DML query or a true empty result.
          setSqlQueryResult(null);
          if (status && status.description !== 'Accepted') {
            finalOutput += `Status: ${status.description}\n`;
          } else {
            finalOutput = '✓ Query executed successfully. No rows returned.';
          }
        }
      } else {
        // ── Non-SQL result path (original logic) ──────────────────────────────
        if (stdout)  finalOutput += `${atob(stdout)}\n`;
        if (stderr)  finalOutput += `Error:\n${atob(stderr)}\n`;
        if (message) finalOutput += `Message:\n${atob(message)}\n`;
        if (status && status.description !== "Accepted") {
          finalOutput += `Status: ${status.description}\n`;
        }
      }

      setTerminalOutput(isSQLRun ? finalOutput : (finalOutput || "Success (no output)"));

      setTestCaseResults(tcResults || []);

      // Update last execution metadata
      let executionStatus = "success";
      if (status.id === 6)                          executionStatus = "compile_error";
      else if (status.id >= 7 && status.id <= 12)  executionStatus = "runtime_error";
      else if (status.id !== 3)                     executionStatus = "error";

      setLastExecutionMetadata({
        questionType:          "coding",
        selectedLanguage:      selectedLang.label,
        sourceCode:            code,
        executionOutput:       stdout         ? atob(stdout)         : null,
        compileError:          compile_output ? atob(compile_output) : null,
        runtimeError:          stderr         ? atob(stderr)         : null,
        terminalOutput:        finalOutput,
        executionStatus:       executionStatus,
        executedAt:            new Date(),
        testCaseResults: tcResults || [],
        testCaseSummary: tcSummary || null,
        // SQL-specific metadata
        ...(isSQLRun && {
          schemaAutoLoaded:     !!sqlSetupScript,
          sqlSetupScript:       sqlSetupScript || null,
          sqlResult:            res.data.sqlResult,
          sqlHiddenTestResults: hiddenResults || [],
          sqlTierScore:         tierScore !== undefined ? tierScore : null,
          sqlFeedback:          feedbackMsgs || [],
        }),
      });

    } catch (err) {
      console.error(err);
      setTerminalOutput(`Error: ${err.response?.data?.message || err.message}`);
    } finally {
      setIsExecuting(false);
    }
  };

  const stopCode = () => {
    setIsExecuting(false);
    setTerminalOutput(prev => prev + "\nExecution stopped.");
  };

  const clearTerminal = () => {
    setTerminalOutput("");
  };

  useEffect(() => {
    const maxTerminal = Math.max(50, Math.floor(compilerPanelHeight * 0.55));
    if (terminalHeight > maxTerminal) setTerminalHeight(maxTerminal);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compilerPanelHeight]);

  const handleCompilerResizeStart = (e) => {
    e.preventDefault();
    const startY = e.clientY;
    const startH = compilerPanelHeight;
    const minH = 220;
    const maxH = Math.floor(window.innerHeight * 0.75);

    const onMove = (ev) => {
      const delta = startY - ev.clientY;
      setCompilerPanelHeight(Math.min(Math.max(startH + delta, minH), maxH));
    };

    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    document.body.style.cursor = "ns-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const saveCode = async () => {
    if (!questions[currentQuestionIndex]) return;

    const currentQ = questions[currentQuestionIndex];
    const isFrontendQ = lockedCompiler === "frontend";
    const codeToSave = isFrontendQ
      ? `<!-- HTML -->\n${htmlCode}\n\n/* CSS */\n${cssCode}`
      : code;
    const langLabel = isFrontendQ
      ? "HTML/CSS"
      : (SUPPORTED_LANGUAGES.find(l => l.value === language)?.label || language);

    // Issue 1: capture rendered preview output; treat any non-empty HTML/CSS as "executed"
    // regardless of whether console.log was called.
    const isFrontendCode = isFrontendQ && (htmlCode.trim().length > 0 || cssCode.trim().length > 0);
    let frontendRenderedText = null;
    let frontendSnapshot = null;
    if (isFrontendQ && previewIframeRef.current) {
      try {
        frontendRenderedText = previewIframeRef.current.contentDocument?.body?.innerText?.trim() || null;
        // Capture the full HTML content of the iframe for report rendering
        frontendSnapshot = previewIframeRef.current.contentDocument?.documentElement?.outerHTML || null;
      } catch { /* sandbox cross-origin fallback */ }
    }
    const frontendConsolePart = consoleLogs.length > 0
      ? consoleLogs.map(l => `[${l.level.toUpperCase()}] ${l.message}`).join('\n')
      : null;
    const frontendOutput = isFrontendQ
      ? ([
          frontendRenderedText ? `=== Rendered Preview ===\n${frontendRenderedText}` : null,
          frontendConsolePart  ? `=== Console Logs ===\n${frontendConsolePart}` : null,
        ].filter(Boolean).join('\n\n') || (isFrontendCode ? `=== Rendered Preview ===\n${htmlCode}` : null))
      : null;

      const currentAnswer = {
        question:              currentQ?.text,
        questionIndex:         currentQuestionIndex,
        answer:                codeToSave || "No code written",
        questionType:          "coding",
        selectedLanguage:      langLabel,
        sourceCode:            codeToSave,
        frontendSnapshot:      frontendSnapshot,
        executionOutput:       frontendOutput || lastExecutionMetadata?.executionOutput    || null,
        compileError:          lastExecutionMetadata?.compileError           || null,
        runtimeError:          lastExecutionMetadata?.runtimeError           || null,
        terminalOutput:        frontendOutput || lastExecutionMetadata?.terminalOutput     || null,
        executionStatus:       isFrontendQ
          ? (isFrontendCode ? "success" : "not_executed")
          : (lastExecutionMetadata?.executionStatus || "not_executed"),
        executedAt:            isFrontendQ
          ? (isFrontendCode ? new Date() : null)
          : (lastExecutionMetadata?.executedAt || null),
        testCaseResults:  lastExecutionMetadata?.testCaseResults || testCaseResults,
        testCaseSummary:  lastExecutionMetadata?.testCaseSummary || null,
        minimumPassThreshold:  currentQ?.minimumPassThreshold                || 2,
        consoleLogs:           consoleLogs,
        timeTaken:             Math.floor((Date.now() - questionStartTime) / 1000),
        // SQL-specific (only set when language was SQL)
        ...(lastExecutionMetadata?.schemaAutoLoaded !== undefined && {
          schemaAutoLoaded: lastExecutionMetadata.schemaAutoLoaded,
        }),
      };

    setAnswers(prev => {
      // Deduplicate by questionIndex (preferred) or question text as fallback.
      // Using questionIndex prevents a question with the same text as another
      // from being incorrectly removed on re-save.
      const existing = prev.filter(a =>
        a.questionIndex !== undefined
          ? a.questionIndex !== currentQuestionIndex
          : a.question !== currentQ.text
      );
      return [...existing, currentAnswer];
    });

    // Save execution metadata to backend if available
    if (lastExecutionMetadata) {
      try {
        await axios.post(`${API_BASE_URL}/interviewRoutes/save-execution-metadata`, {
          applicationId: interviewId,
          metadata: lastExecutionMetadata
        });
      } catch (err) {
        console.error("Failed to save execution metadata on saveCode:", err);
      }
    }

    // Trigger an immediate draft save so this answer survives a reload
    isDirtyRef.current = true;
    performAutoSave('manual').catch(() => {});

    setSnackbar({
      open: true,
      message: "Answer saved successfully.",
      severity: "success"
    });
  };
  const [missingFaceFrames, setMissingFaceFrames] = useState(0);
  const [faceViolationCount, setFaceViolationCount] = useState(0);
  const [headTurnCount, setHeadTurnCount] = useState(0);
  const [overlappingSpeechCount, setOverlappingSpeechCount] = useState(0);
  const [agreed, setAgreed] = useState(false);
  const [screenShareError, setScreenShareError] = useState('');

  const [behaviorMetrics, setBehaviorMetrics] = useState({
    totalResponseTime: 0,
    questionCount: 0
  });

  const [violations, setViolations] = useState({
    tabSwitch: 0,
    eyeGazeAway: 0,
    faceMissing: 0,
    backgroundVoice: 0,
    vpnDetected: 0,
    deviceChange: 0,
    ipChange: 0,
    multipleFaces: 0,
    noFaceDetected: 0
  });
  const [showIdleModal, setShowIdleModal] = useState(false);
  const lastActivityRef = useRef(Date.now());
  const [violationModal, setViolationModal] = useState({
    open: false,
    title: '',
    message: '',
    type: 'warning',
    onConfirm: () => {}
  });
  const [events, setEvents] = useState([]);
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [fingerprint, setFingerprint] = useState(null);
  const [questionStartTime, setQuestionStartTime] = useState(null);
  const [preStartTimeLeft, setPreStartTimeLeft] = useState(null);
  const violationsRef = useRef({
    tabSwitch: 0,
    eyeGazeAway: 0,
    faceMissing: 0,
    backgroundVoice: 0,
    vpnDetected: 0,
    deviceChange: 0,
    ipChange: 0,
    multipleFaces: 0,
    noFaceDetected: 0
  });

  const isSubmittingRef = useRef(false); // 🔒 CRITICAL: Instantly locks the termination signal
  const popupShownRef = useRef(false);
  const timerRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recognitionRef = useRef(null);
  const synthRef = window.speechSynthesis;
  const finalTranscriptRef = useRef("");
  const lastSpeechTimeRef = useRef(null);
  const pauseDurationsRef = useRef([]);
  const alertShownRef = useRef(false);
  const headTurnLockRef = useRef(false);
  const aiSpeakingRef = useRef(false);
  const audioCooldownRef = useRef(0);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const networkIntervalRef = useRef(null);
  const previewIframeRef = useRef(null);
  const eventsRef = useRef([]);
  const idleWarningsRef = useRef(0);
  const totalResponsesRef = useRef(0);
  const headTurnFramesRef = useRef(0);
  const screenShareStopTimeRef = useRef(null);
  const screenShareWarningActiveRef = useRef(false);
  const screenShareTerminateTimerRef = useRef(null);

  // ── Auto-save state & refs ────────────────────────────────────────────────
  // 'idle' | 'saving' | 'saved' | 'offline' | 'error'
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle');
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const isDirtyRef = useRef(false);
  const autoSaveIntervalRef = useRef(null);
  const pendingOfflineDraftRef = useRef(null);

  // The draft key is the URL param (interviewId for coding test, sessionToken for tech/HR)
  const draftId = interviewId || sessionToken;
  const draftLocalKey = `exihire_draft_${draftId}`;

  const buildDraftPayload = () => ({
    candidateCode: code,
    selectedLanguage: language,
    htmlCode,
    cssCode,
    questionIndex: currentQuestionIndex,
    lockedCompiler,
    answers,
  });

  const saveDraftToLocalStorage = (payload) => {
    try {
      localStorage.setItem(draftLocalKey, JSON.stringify({ ...payload, savedAt: Date.now() }));
    } catch { /* storage full — ignore */ }
  };

  const performAutoSave = async (source = 'autosave') => {
    if (!isDirtyRef.current && source === 'autosave') return;
    if (!draftId) return;

    const payload = buildDraftPayload();
    saveDraftToLocalStorage(payload);

    try {
      setAutoSaveStatus('saving');
      await axios.post(`${API_BASE_URL}/interviewRoutes/save-draft`, {
        interviewId: draftId,
        draft: payload,
        saveSource: source,
      });
      isDirtyRef.current = false;
      setAutoSaveStatus('saved');
      setLastSavedAt(new Date());
    } catch {
      if (!navigator.onLine) {
        setAutoSaveStatus('offline');
        pendingOfflineDraftRef.current = payload;
      } else {
        setAutoSaveStatus('error');
      }
    }
  };

  // Mark dirty whenever editor content changes while interview is active
  useEffect(() => {
    if (interviewStarted) {
      isDirtyRef.current = true;
    }
  }, [code, htmlCode, cssCode, transcript]); // eslint-disable-line react-hooks/exhaustive-deps

  // 10-second auto-save interval — starts when interview starts, stops on cleanup
  useEffect(() => {
    if (!interviewStarted) return;
    autoSaveIntervalRef.current = setInterval(() => {
      performAutoSave('autosave');
    }, 10000);
    return () => {
      if (autoSaveIntervalRef.current) clearInterval(autoSaveIntervalRef.current);
    };
  }, [interviewStarted]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync pending offline draft when connection is restored
  useEffect(() => {
    const handleOnline = () => {
      if (pendingOfflineDraftRef.current) {
        performAutoSave('offline-sync');
        pendingOfflineDraftRef.current = null;
      }
    };
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Restore saved draft when interview loads (called after fetchContext succeeds)
  const restoreDraftIfExists = async () => {
    if (!draftId) return;
    let draft = null;
    try {
      const res = await axios.get(`${API_BASE_URL}/interviewRoutes/get-draft/${draftId}`);
      draft = res.data?.draft || null;
    } catch {
      // Fall back to localStorage
      try {
        const local = localStorage.getItem(draftLocalKey);
        if (local) draft = JSON.parse(local);
      } catch { /* ignore */ }
    }
    if (!draft) return;
    if (draft.selectedLanguage) setLanguage(draft.selectedLanguage);
    if (draft.candidateCode)    setCode(draft.candidateCode);
    if (draft.htmlCode)         setHtmlCode(draft.htmlCode);
    if (draft.cssCode)          setCssCode(draft.cssCode);
    if (draft.lockedCompiler)   setLockedCompiler(draft.lockedCompiler);
    if (Array.isArray(draft.answers) && draft.answers.length > 0) setAnswers(draft.answers);
    setLastSavedAt(draft.last_saved_at ? new Date(draft.last_saved_at) : (draft.savedAt ? new Date(draft.savedAt) : null));
    setAutoSaveStatus('saved');
    setSnackbar({ open: true, message: 'Previous progress restored.', severity: 'info' });
  };

  const clearDraft = async () => {
    localStorage.removeItem(draftLocalKey);
    try {
      await axios.delete(`${API_BASE_URL}/interviewRoutes/clear-draft/${draftId}`);
    } catch { /* non-critical */ }
  };
  // ─────────────────────────────────────────────────────────────────────────────

  // ── Per-question editor snapshot (auto-save answer recovery) ─────────────────
  // Tracks the latest editor state for EVERY coding/SQL/frontend question the
  // candidate visits, keyed by questionIndex. Survives question navigation so
  // answers that were never manually saved can still be included in the final
  // submission payload.
  const questionEditorSnapshotsRef = useRef({});

  // Update the snapshot for the current coding question whenever the editor changes
  useEffect(() => {
    if (currentQuestionIndex < 0 || !interviewStarted) return;
    const currentQ = questions[currentQuestionIndex];
    if (!currentQ) return;
    const isCodingQ = currentQ.type === 'coding' || currentQ.type === 'query' || !!lockedCompiler;
    if (!isCodingQ) return;

    const isFrontend = lockedCompiler === 'frontend';
    const codeContent = isFrontend
      ? `<!-- HTML -->\n${htmlCode}\n\n/* CSS */\n${cssCode}`
      : code;
    if (!codeContent || !codeContent.trim()) return;

    const langLabel = isFrontend
      ? 'HTML/CSS'
      : (SUPPORTED_LANGUAGES.find(l => l.value === language)?.label || language || '');

    questionEditorSnapshotsRef.current[currentQuestionIndex] = {
      ...(questionEditorSnapshotsRef.current[currentQuestionIndex] || {}),
      question: currentQ.text,
      questionIndex: currentQuestionIndex,
      codeContent,
      selectedLanguage: langLabel,
      htmlCode,
      cssCode,
      lockedCompiler,
      consoleLogs: consoleLogs || [],
    };
  }, [code, htmlCode, cssCode, currentQuestionIndex, language, lockedCompiler, consoleLogs]); // eslint-disable-line react-hooks/exhaustive-deps

  // Capture execution results into the per-question snapshot whenever code is run
  useEffect(() => {
    if (!lastExecutionMetadata || currentQuestionIndex < 0) return;
    questionEditorSnapshotsRef.current[currentQuestionIndex] = {
      ...(questionEditorSnapshotsRef.current[currentQuestionIndex] || {}),
      executionMeta: lastExecutionMetadata,
    };
  }, [lastExecutionMetadata]); // eslint-disable-line react-hooks/exhaustive-deps
  // ─────────────────────────────────────────────────────────────────────────────

  // Capture console.log / warn / error / info from the frontend preview iframe
  useEffect(() => {
    const handler = (e) => {
      if (e.data && e.data.__exihire) {
        setConsoleLogs(prev => [
          ...prev,
          { level: e.data.level, message: e.data.message, ts: Date.now() },
        ]);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  // ── Random capture monitoring (isolated layer — DO NOT move or interleave) ──
  const randomCaptureControls = useRandomCapture({
    interviewId: applicationId || interviewId,
    videoRef,
    isActive: interviewStarted && stage === 'questioning' && !isTerminated,
    apiBaseUrl: API_BASE_URL,
    onViolation: (type, meta) => {
      setEvents(prev => [...prev, {
        type,
        timestamp: new Date().toISOString(),
        source: 'random_capture',
        ...meta,
      }]);
    },
  });
  // ─────────────────────────────────────────────────────────────────────────────

  // Must be called from a direct user-gesture handler (button onClick).
  // Validates that the user selects the entire screen, not a browser tab or window.
  const handleRequiredScreenShare = async () => {
    setScreenShareError('');
    const result = await randomCaptureControls.requestScreenShare();
    if (!result || !result.granted) {
      if (result?.reason === 'not_monitor') {
        setScreenShareError('Please select "Entire Screen" — browser tabs and application windows are not accepted for this interview.');
      } else if (result?.reason === 'permission_denied') {
        setScreenShareError('Screen sharing permission was denied. Please allow screen access and try again.');
      } else {
        setScreenShareError('Screen sharing is required to start the interview. Please try again.');
      }
    }
  };

  const stopNetworkMonitoring = () => {
    if (networkIntervalRef.current) {
      clearInterval(networkIntervalRef.current);
      networkIntervalRef.current = null;
    }
  };

  useEffect(() => {
    if (stage === "finished" || isTerminated) {
      stopNetworkMonitoring();
    }
  }, [stage, isTerminated]);

  const resetIdleTimer = () => {
    lastActivityRef.current = Date.now();
    if (showIdleModal) setShowIdleModal(false);
  };

  useEffect(() => {
    const handleActivity = () => resetIdleTimer();

    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('mousedown', handleActivity);
    window.addEventListener('keydown', handleActivity);

    const idleInterval = setInterval(() => {
      if (interviewStarted && stage === 'questioning' && !isTerminated && !showIdleModal) {
        const now = Date.now();
        const inactiveTime = now - lastActivityRef.current;

        if (inactiveTime >= 45000) { // 45 seconds
          setShowIdleModal(true);
          idleWarningsRef.current += 1;
        }
      }
    }, 1000);

    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('mousedown', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      clearInterval(idleInterval);
    };
  }, [interviewStarted, stage, isTerminated, showIdleModal]);

  const showViolation = (title, message, type = 'warning', onConfirm = null) => {
    setViolationModal({
      open: true,
      title,
      message,
      type,
      onConfirm: () => {
        setViolationModal(prev => ({ ...prev, open: false }));
        if (onConfirm) onConfirm();
      }
    });
  };

  const startNetworkMonitoring = () => {
    // Clear any existing interval
    stopNetworkMonitoring();

    networkIntervalRef.current = setInterval(async () => {
      try {
        const res = await axios.post(
          `${API_BASE_URL}/interviewRoutes/check-network`,
          { interviewId },
          { withCredentials: true }
        );

        console.log("NETWORK CHECK (every 30s):", res.data);

        // 🔥 FIX HERE
        if (res.data.isSuspicious) {
          showViolation("Network Alert", "⚠️ Network change detected (VPN/Proxy)");
          setViolations(prev => {
            const updated = {
              ...prev,
              vpnDetected: prev.vpnDetected + 1
            };
            violationsRef.current = updated;
            return updated;
          });
          setEvents(prev => {
            if (!interviewStarted || timeLeftRef.current === null) return prev;
            const currentTime = timeLeftRef.current ?? 0;
            const updated = [...prev, { type: "vpnDetected", time: currentTime }];
            eventsRef.current = updated;
            return updated;
          });// Rough time estimation or use a better one
        }

      } catch (err) {
        console.error("Network check failed", err);
      }
    }, 30000);// every 30 seconds
  };

  useEffect(() => {
    timeLeftRef.current = timeLeft;
  }, [timeLeft]);

  // Background Voice Detection Loop
  useEffect(() => {
    let animationFrameId;
    let lastFrequencyData = null;
    let consecutiveOverlapFrames = 0;

    const detectOverlappingSpeech = () => {
      if (!analyserRef.current || !interviewStarted) {
        consecutiveOverlapFrames = 0;
        animationFrameId = requestAnimationFrame(detectOverlappingSpeech);
        return;
      }

      if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume();
      }

      const analyser = analyserRef.current;
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      analyser.getByteFrequencyData(dataArray);

      // 1. RMS (volume level)
      let sumSquares = 0;
      for (let i = 0; i < bufferLength; i++) {
        const val = dataArray[i] / 255;
        sumSquares += val * val;
      }
      const rms = Math.sqrt(sumSquares / bufferLength);

      // Noise Filtering: Ignore low RMS values (fan, silence)
      const minVolumeThreshold = 0.05; // Slightly more sensitive
      if (rms < minVolumeThreshold) {
        consecutiveOverlapFrames = 0;
        animationFrameId = requestAnimationFrame(detectOverlappingSpeech);
        return;
      }

      // 2. Spectral Spread (number of active frequency bins)
      let activeBins = 0;
      const magnitudeThreshold = 35; // Slightly more sensitive for active bin
      for (let i = 0; i < bufferLength; i++) {
        if (dataArray[i] > magnitudeThreshold) activeBins++;
      }
      const spectralSpread = activeBins / bufferLength;

      // 3. Instability (frame-to-frame variation)
      let instability = 0;
      if (lastFrequencyData) {
        let diff = 0;
        for (let i = 0; i < bufferLength; i++) {
          diff += Math.abs(dataArray[i] - lastFrequencyData[i]);
        }
        instability = diff / (bufferLength * 255);
      }
      lastFrequencyData = new Uint8Array(dataArray);

      // 4. Speech Ratio (mid-frequency energy vs total energy)
      let midEnergy = 0;
      let totalEnergy = 0;
      // Mid-frequency roughly bins 14 to 160 for FFT 2048
      for (let i = 0; i < bufferLength; i++) {
        totalEnergy += dataArray[i];
        if (i >= 14 && i <= 160) {
          midEnergy += dataArray[i];
        }
      }
      const speechRatio = totalEnergy > 0 ? midEnergy / totalEnergy : 0;

      // --- DETECTION LOGIC ---
      // Goal: Detect layered audio (Voice + Background OR Multiple Voices)

      const isLoud = rms > 0.12;
      const isSpeechLike = speechRatio > 0.55;
      const isWide = spectralSpread > 0.25;
      const isUnstable = instability > 0.04;
      const isHighSpread = spectralSpread > 0.25;
      const isHighInstability = instability > 0.04;

      // Trigger if:
      // 1. Loud AND (Speech-like AND Wide AND Unstable) -> Multiple voices
      // 2. High spread AND High instability -> Layered/Background noise

      const isTriggerCondition = isLoud && (
        (isSpeechLike && isWide && isUnstable) ||
        (isHighSpread && isHighInstability)
      );

      // --- PERSISTENCE ---
      // Trigger only if condition holds for multiple consecutive frames (e.g., 5–10 frames)
      if (isTriggerCondition && Date.now() > audioCooldownRef.current) {
        consecutiveOverlapFrames++;
        if (consecutiveOverlapFrames >= 8) { // Between 5 and 10 frames
          consecutiveOverlapFrames = 0; // Reset after trigger to avoid immediate double-trigger
          audioCooldownRef.current = Date.now() + 5000; // 5 second cooldown

          console.warn("⚠️ REAL-TIME AUDIO DETECTION TRIGGERED", {
            rms,
            spectralSpread,
            instability,
            speechRatio,
            isLoud,
            isHighSpread,
            isHighInstability
          });

          setOverlappingSpeechCount(prev => {
            const newCount = prev + 1;
            setViolations(v => {
              const updated = { ...v, backgroundVoice: newCount };
              violationsRef.current = updated;
              return updated;
            });
            setEvents(prev => {
              if (!interviewStarted || timeLeftRef.current === null) return prev;
              const currentTime = timeLeftRef.current ?? 0;
              const updated = [...prev, { type: "backgroundVoice", time: currentTime }];
              eventsRef.current = updated;
              return updated;
            });
            if (newCount === 1) {
              showViolation("Audio Warning", "Warning: Background audio or multiple voices detected. Please ensure a quiet environment.");
            } else if (newCount === 2) {
              showViolation("Final Audio Warning", "Final Warning: Repeated background audio detected. The interview will terminate if this continues.");
            } else if (newCount >= 3) {
              if (!isTerminated) {
                setIsTerminated(true);
                showViolation("Interview Terminated", "Interview terminated due to persistent background audio/voices.", "termination", () => {
                  handleSubmitInterview(true);
                });
              }
            }
            return newCount;
          });
        }
      } else {
        consecutiveOverlapFrames = Math.max(0, consecutiveOverlapFrames - 1); // Decay counter
      }

      animationFrameId = requestAnimationFrame(detectOverlappingSpeech);
    };

    if (interviewStarted) {
      animationFrameId = requestAnimationFrame(detectOverlappingSpeech);
    }

    return () => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
  }, [interviewStarted, isListening]);

  useEffect(() => {
    fetchContext();
      const loadFingerprint = async () => {
          const fp = await FingerprintJS.load();
          const result = await fp.get();
          setFingerprint(result.visitorId);
          console.log("Browser Fingerprint:", result.visitorId);
      };
      loadFingerprint();

      return () => {
        stopStream();
        if (synthRef) synthRef.cancel();
        if (recognitionRef.current) recognitionRef.current.stop();
        if (timerRef.current) clearInterval(timerRef.current);
      };
  }, []);




  useEffect(() => {

    const loadModels = async () => {
      await faceapi.nets.tinyFaceDetector.loadFromUri("/models");
      await faceapi.nets.faceLandmark68Net.loadFromUri("/models");

      setModelsLoaded(true);
    };

    loadModels();

  }, []);
  useEffect(() => {
    if (error === "expired") {
      stopStream();

      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch {}
      }

      if (synthRef) synthRef.cancel();

      if (timerRef.current) clearInterval(timerRef.current);
    }
  }, [error]);



  useEffect(() => {
    console.log("DEBUG AUTO START:", {
      cameraReady,
      interviewStarted,
      error,
      preStartTimeLeft
    });

    if (cameraReady && !interviewStarted && !error && preStartTimeLeft <= 0 && randomCaptureControls.screenShareIsMonitor) {
      console.log("🔥 STARTING INTERVIEW NOW");
      startInterview();
    }
  }, [cameraReady, interviewStarted, error, preStartTimeLeft, randomCaptureControls.screenShareIsMonitor]);

  useEffect(() => {
    const interval = setInterval(() => {

      // 🔥 Detect stuck mic
      if (
        interviewStarted &&
        stage === "questioning" &&
        !aiSpeakingRef.current &&
        isListening &&
        transcript.trim() === ""
      ) {
        console.warn("⚠️ Mic stuck → restarting");

        try {
          recognitionRef.current?.stop();
        } catch {}

        setTimeout(() => {
          startListening();
        }, 500);
      }

    }, 4000);

    return () => clearInterval(interval);
  }, [interviewStarted, stage, isListening, transcript]);

  useEffect(() => {

    let faceInterval;

    if (isCameraOn && modelsLoaded){


      if (isCameraOn && modelsLoaded) {
        setTimeout(() => {
          // start interval here
        }, 2000);
      }
      faceInterval = setInterval(async () => {

        if (isTerminated) return;
        if (!videoRef.current || videoRef.current.readyState !== 4) return;

        const detections = await faceapi.detectAllFaces(
          videoRef.current,
          new faceapi.TinyFaceDetectorOptions({
            inputSize: 320,
            scoreThreshold: 0.5
          })
        ).withFaceLandmarks();

        console.log("Faces detected:", detections.length);
        if (detections.length === 1) {
          setMissingFaceFrames(0);
        }
        if (detections.length === 0 && interviewStarted) {

          setMissingFaceFrames(prev => {

            const frames = prev + 1;

            // ignore short glitches
            if (frames < 4) return frames;

            setFaceViolationCount(v => {

              const newCount = v + 1;
              setViolations(vi => {
                const updated = { ...vi, faceMissing: newCount };
                violationsRef.current = updated;
                return updated;
              });
              setEvents(prev => {
                if (!interviewStarted || timeLeftRef.current === null) return prev;
                const currentTime = timeLeftRef.current ?? 0;
                const updated = [...prev,{ type: "faceMissing", time: currentTime }];
                eventsRef.current = updated;
                return updated;
              });

              if (newCount === 1 || newCount === 2) {
                showViolation("Face Detection Warning", "Face not detected. Please stay in front of the camera.");
              }

              if (newCount >= 3) {

                showViolation("Interview Terminated", "Interview terminated because your face was not detected.", "termination", () => {
                  if (timerRef.current) clearInterval(timerRef.current);

                  if (recognitionRef.current) {
                    try { recognitionRef.current.stop(); } catch {}
                  }

                  stopStream();

                  if (synthRef) synthRef.cancel();

                  setStage("finished");
                  setInterviewStarted(false);
                  setIsListening(false);
                  setIsTerminated(true);

                  handleSubmitInterview(true);
                });
              }

              return newCount;

            });

            return 0;

          });

        }

        if (detections.length > 1 && interviewStarted) {

          setFaceViolationCount(prev => {

            const newCount = prev + 1;
            setViolations(vi => {
              const updated = { ...vi, multipleFaces: newCount };
              violationsRef.current = updated;
              return updated;
            });
            setEvents(prev => {
              if (!interviewStarted || timeLeftRef.current === null) return prev;
              const currentTime = timeLeftRef.current ?? 0;
              const updated = [...prev, { type: "multipleFaces", time: currentTime }];
              eventsRef.current = updated;
              return updated;
            });

            if (newCount === 1 || newCount === 2) {
              if (!alertShownRef.current) {
                alertShownRef.current = true;

                showViolation("Multiple Faces Detected", "Multiple faces detected. Please ensure only one person is present.", "warning", () => {
                   alertShownRef.current = false;
                });
              }
            }

            if (newCount >= 3) {

              showViolation("Interview Terminated", "Interview terminated due to multiple people detected.", "termination", () => {
                if (timerRef.current) clearInterval(timerRef.current);

                if (recognitionRef.current) {
                  try { recognitionRef.current.stop(); } catch {}
                }

                stopStream();

                if (synthRef) synthRef.cancel();

                setStage("finished");
                setInterviewStarted(false);
                setIsListening(false);

                setIsTerminated(true);

                handleSubmitInterview(true);
              });
            }

            return newCount;

          });

        }
        if (detections.length === 1 && interviewStarted) {

          const landmarks = detections[0].landmarks;

          const nose = landmarks.getNose();
          const jaw = landmarks.getJawOutline();

          const noseX = nose[3].x;
          const jawLeft = jaw[0].x;
          const jawRight = jaw[16].x;

          const faceCenter = (jawLeft + jawRight) / 2;
          const deviation = Math.abs(noseX - faceCenter);



          // reset lock if face returns to center
          if (deviation <= 50) {
            headTurnLockRef.current = false;
          }

          // detect head turn
          if (deviation > 60 && !headTurnLockRef.current) {

            headTurnLockRef.current = true;

            setHeadTurnCount(prev => {

              const newCount = prev + 1;
              setViolations(vi => {
                const updated = { ...vi, eyeGazeAway: newCount };
                violationsRef.current = updated;
                return updated;
              });
              setEvents(prev => {
                if (!interviewStarted || timeLeftRef.current === null) return prev;
                const currentTime = timeLeftRef.current ?? 0;
                const updated = [...prev, { type: "eyeGazeAway", time: currentTime }];
                eventsRef.current = updated;
                return updated;
              });

              if (newCount === 1 || newCount === 2) {

                if (!alertShownRef.current) {

                  alertShownRef.current = true;

                  showViolation("Head Movement Warning", `Warning ${newCount}/2: Please keep your face towards the screen.`, "warning", () => {
                    alertShownRef.current = false;
                  });

                }

              }

              if (newCount >= 3) {

                showViolation("Interview Terminated", "Interview terminated due to repeated head turning.", "termination", () => {
                  if (timerRef.current) clearInterval(timerRef.current);

                  if (recognitionRef.current) {
                    try { recognitionRef.current.stop(); } catch {}
                  }

                  stopStream();

                  if (synthRef) synthRef.cancel();

                  setStage("finished");
                  setInterviewStarted(false);
                  setIsListening(false);
                  setIsTerminated(true);

                  handleSubmitInterview(true);
                });
              }

              return newCount;

            });

          }
        }


      }, 2500); // check every 2 seconds

    }

    return () => clearInterval(faceInterval);

  }, [isCameraOn, interviewStarted, stage, modelsLoaded, isSubmitting]);

  useEffect(() => {

    const handleTabSwitch = () => {

      if (document.hidden && interviewStarted && stage === "questioning" && !isSubmittingRef.current) {

        setTabSwitchCount(prev => {

          const newCount = prev + 1;
          setViolations(vi => {
            const updated = { ...vi, tabSwitch: newCount };
            violationsRef.current = updated;   // 🔥 CRITICAL LINE
            return updated;
          });
          setEvents(prev => {
            if (!interviewStarted || timeLeftRef.current === null) return prev;
            const currentTime = timeLeftRef.current ?? 0;
            const updated = [...prev, { type: "tabSwitch", time: currentTime }];
            eventsRef.current = updated;
            return updated;
          });

          if (newCount === 1 || newCount === 2) {
            showViolation("Tab Switch Warning", `Warning ${newCount}/2: Do not switch tabs during the interview.`);
          }

          if (newCount >= 3) {
            showViolation("Interview Terminated", "Interview terminated due to multiple tab switches.", "termination", () => {
              // stop timer
              if (timerRef.current) clearInterval(timerRef.current);

              // stop speech recognition
              if (recognitionRef.current) {
                try {
                  recognitionRef.current.stop();
                } catch {}
              }

              // stop camera + mic
              stopStream();

              // stop AI speech
              if (synthRef) synthRef.cancel();

              // disable interview UI
              setStage("finished");
              setInterviewStarted(false);
              setIsListening(false);

              // submit interview
              setIsTerminated(true);
              handleSubmitInterview(true);
            });
          }

          return newCount;

        });

      }

    };

    document.addEventListener("visibilitychange", handleTabSwitch);

    return () => {
      document.removeEventListener("visibilitychange", handleTabSwitch);
    };

  }, [interviewStarted, stage, isTerminated, isSubmittingRef.current ]);

  // ── Screen share monitoring during interview ───────────────────────────────
  // If screen sharing is lost during the questioning stage, warn the candidate
  // and give them 30 seconds to restore it before terminating the interview.
  useEffect(() => {
    if (!interviewStarted || stage !== 'questioning' || isTerminated) {
      if (screenShareTerminateTimerRef.current) {
        clearTimeout(screenShareTerminateTimerRef.current);
        screenShareTerminateTimerRef.current = null;
      }
      screenShareStopTimeRef.current = null;
      screenShareWarningActiveRef.current = false;
      return;
    }

    if (randomCaptureControls.screenShareIsMonitor) {
      // Share is active — clear any pending termination
      if (screenShareTerminateTimerRef.current) {
        clearTimeout(screenShareTerminateTimerRef.current);
        screenShareTerminateTimerRef.current = null;
      }
      screenShareStopTimeRef.current = null;
      screenShareWarningActiveRef.current = false;
      return;
    }

    // Screen share is NOT active during the interview
    if (screenShareWarningActiveRef.current) return; // Already warned, timer is running

    screenShareWarningActiveRef.current = true;
    screenShareStopTimeRef.current = Date.now();

    setEvents(prev => [...prev, {
      type: 'SCREEN_SHARE_STOPPED',
      timestamp: new Date().toISOString(),
      source: 'screen_share_monitor',
      interviewId,
    }]);

    showViolation(
      'Screen Sharing Stopped',
      'Your screen share has been interrupted. Please click OK and then re-enable "Entire Screen" sharing immediately. Failure to restore within 30 seconds will terminate the interview.',
      'warning',
      () => { handleRequiredScreenShare(); }
    );

    screenShareTerminateTimerRef.current = setTimeout(() => {
      if (!screenShareWarningActiveRef.current) return; // Already restored — skip

      const duration = Math.round((Date.now() - (screenShareStopTimeRef.current || Date.now())) / 1000);
      setEvents(prev => [...prev, {
        type: 'SCREEN_SHARE_TERMINATED',
        timestamp: new Date().toISOString(),
        source: 'screen_share_monitor',
        interviewId,
        durationWithoutSharing: duration,
      }]);

      showViolation(
        'Interview Terminated',
        'Interview terminated because screen sharing was not restored within 30 seconds.',
        'termination',
        () => {
          if (timerRef.current) clearInterval(timerRef.current);
          try { recognitionRef.current?.stop(); } catch {}
          stopStream();
          if (synthRef) synthRef.cancel();
          setStage('finished');
          setInterviewStarted(false);
          setIsListening(false);
          setIsTerminated(true);
          handleSubmitInterview(true);
        }
      );
    }, 30000);

    return () => {
      if (screenShareTerminateTimerRef.current) {
        clearTimeout(screenShareTerminateTimerRef.current);
        screenShareTerminateTimerRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [randomCaptureControls.screenShareIsMonitor, interviewStarted, stage, isTerminated]);

  useEffect(() => {
    if (timeLeft === 0) {
      if (timerRef.current) clearInterval(timerRef.current);
      handleTimeExpired();
    }
  }, [timeLeft]);

  useEffect(() => {
    if (!context?.scheduled_at) return;
    console.log("SCHEDULED TIME:", context?.scheduled_at);

    const interval = setInterval(() => {
      const now = new Date();
      const start = new Date(context.scheduled_at);
      const diff = Math.floor((start - now) / 1000);

      if (diff <= 0) {
        setPreStartTimeLeft(0);
        clearInterval(interval);
      } else {
        setPreStartTimeLeft(diff);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [context]);

  const handleTimeExpired = () => {

    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch {}
    }

    setIsListening(false);

    if (timerRef.current) clearInterval(timerRef.current);

    // ✅ save answer
    if (transcript.trim()) {
      setAnswers(prev => [
        ...prev,
        {
          question: questions[currentQuestionIndex]?.text,
          answer: transcript.trim()
        }
      ]);
    }



    setStage("finished");

    speak(`Time is up! Thank you for participating in the ${getInterviewTitle()}. Your responses have been recorded.`);
  };

  const startTimer = (minutes) => {
    const seconds = minutes * 60;

    // prevent duplicate timers
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    setTimeLeft(seconds);

    timerRef.current = setInterval(() => {
      setElapsedTime(prev => prev + 1);
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const formatTime = (seconds) => {
    if (seconds === null) return "--:--";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const fetchContext = async () => {
    try {
      const contextUrl = isHrInterviewSession
        ? `${API_BASE_URL}/assessments/hr-interview/context/${sessionToken}`
        : isTechInterviewSession
        ? `${API_BASE_URL}/assessments/technical-interview/context/${sessionToken}`
        : `${API_BASE_URL}/assessments/context/${interviewId}`;

      const res = await axios.get(contextUrl);

      // For token-based sessions, store the resolved applicationId for proctoring
      if (isTechInterviewSession && res.data.applicationId) {
        setApplicationId(res.data.applicationId);
      }

      setContext({
        scheduled_at: res.data.scheduled_at || res.data.scheduledAt,
        jobTitle: res.data.jobTitle,
        resumeUrl: res.data.resumeUrl,
        primarySkills: res.data.primarySkills,
        interviewType: res.data.interviewType,
        level: res.data.level,
        timeLimit: res.data.timeLimit || res.data.time_limit || 30
      });

      // Restore any previously saved draft (best-effort, non-blocking)
      restoreDraftIfExists().catch(() => {});

    } catch (err) {
      const msg = err.response?.data?.message;

      if (msg === "Interview not started yet") {
        setContext({
          scheduled_at:
            err.response?.data?.scheduled_at ||
            err.response?.data?.scheduledAt
        });
        return;
      }

      if (msg === "Interview expired") return setError("expired");
      if (msg === "Interview already completed") return setError("completed");
      if (msg === "Interview already terminated") return setError("terminated");

      setError("Failed to load interview");
    } finally {
      setLoading(false);
    }
  };

  const startStream = async () => {
    console.log("START STREAM CALLED");
    setIsCameraInitializing(true);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: "user" },
        audio: true
      });
      console.log("✅ STREAM RECEIVED:", stream);
      console.log("🎥 TRACKS:", stream.getTracks());

      streamRef.current = stream;

      // Initialize Web Audio API for background voice detection
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (AudioContext) {
          const audioCtx = new AudioContext();
          const analyser = audioCtx.createAnalyser();
          const source = audioCtx.createMediaStreamSource(stream);
          source.connect(analyser);

          analyser.fftSize = 2048;
          audioContextRef.current = audioCtx;
          analyserRef.current = analyser;
          console.log("✅ AUDIO CONTEXT & ANALYSER INITIALIZED");
        }
      } catch (audioErr) {
        console.error("Audio detection init error:", audioErr);
      }

       if (videoRef.current) {
            console.log("📺 VIDEO REF FOUND");

            videoRef.current.srcObject = stream;

            videoRef.current.onloadedmetadata = () => {
              console.log("📦 METADATA LOADED");
            };

            videoRef.current.onloadeddata = () => {
              console.log("📊 DATA LOADED");
            };

            await videoRef.current.play();

            console.log("▶️ VIDEO PLAY STARTED");

            setIsCameraOn(true);
            setIsMicOn(true);
            setCameraReady(true);
            setError("");

          }
    } catch (err) {
      console.error("Media access error:", err);
      if (err.name === "NotAllowedError") setError("Camera permission denied");
      else if (err.name === "NotFoundError") setError("No camera found");
      else setError("Camera error");
    } finally {
      setIsCameraInitializing(false);
    }
  };

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(console.error);
      audioContextRef.current = null;
      analyserRef.current = null;
    }
    setIsCameraOn(false);
    setIsMicOn(false);
  };

  const startInterview = async () => {
    if (error === "terminated") return;
    if (!videoRef.current || !videoRef.current.srcObject) {
        setError("Camera not ready yet");
        return;
      }
      const now = new Date();
      const scheduled = new Date(context?.scheduled_at);

      if (now < scheduled) {
        setError("Interview not started yet");
        return;
      }
      try {
      console.log("START BUTTON CLICKED");

      const screenResolution = getScreenResolution();

      const logStartUrl = isHrInterviewSession
        ? `${API_BASE_URL}/interviewRoutes/hr-interview/log-start/${sessionToken}`
        : isTechInterviewSession
        ? `${API_BASE_URL}/interviewRoutes/technical-interview/log-start/${sessionToken}`
        : `${API_BASE_URL}/interviewRoutes/log-start`;
      const logStartBody = isTechInterviewSession
        ? { fingerprint, screenResolution }
        : { interviewId, fingerprint, screenResolution };

      const res = await axios.post(logStartUrl, logStartBody, { withCredentials: true });

      console.log("FULL RESPONSE:", res.data);

      const isVPN = res.data?.isVPN ?? false;
      const isProxy = res.data?.isProxy ?? false;
      const isSuspicious = res.data?.isSuspicious ?? false;

      console.log("IP:", res.data?.ip);
      console.log("VPN:", isVPN);
      console.log("Proxy:", isProxy);
      console.log("Suspicious:", isSuspicious);

      if (res.data.isSuspicious && !popupShownRef.current) {
        showViolation("Network Alert", "Warning: Suspicious network detected (VPN or IP change). Please avoid switching networks.");
      }

      console.log("INTERVIEW STARTED");

      // ✅ FETCH CONTEXT HERE (CRITICAL FIX)
      const startContextUrl = isHrInterviewSession
        ? `${API_BASE_URL}/assessments/hr-interview/context/${sessionToken}`
        : isTechInterviewSession
        ? `${API_BASE_URL}/assessments/technical-interview/context/${sessionToken}`
        : `${API_BASE_URL}/assessments/context/${interviewId}`;
      const response = await axios.get(startContextUrl);
      console.log("Question API Response:", response.data);

      const qs = response.data.questions || [];

      const normalized = qs.map(q => {
        if (typeof q === "string") return { text: q, type: "normal", compiler: null, topic: "", followUpCount: 0 };

        return {
          text: q.text || q.question || q?.Text?.Question || "",
          type: q.type === "coding" ? "coding" : q.type === "query" ? "query" : "normal",
          compiler: q.compiler || null,
          topic: q.topic || "",
          followUpCount: q.followUpCount || 0,
          visibleTestCases: q.visibleTestCases || q.testCases || [],
          minimumPassThreshold: q.minimumPassThreshold || 2,
          query_questions: q.query_questions || [],
          // SQL-specific fields
          hiddenTestCases: q.hiddenTestCases || null,
          sqlConcepts: q.sqlConcepts || [],
        };
      }).filter(q => q.text);

      // --- Append Query Questions if they exist ---
      // Query questions open the same compiler UI but do NOT require test cases.
      // The compiler is unlocked so the candidate can choose any language.
      const queryQs = (response.data.queryQuestions || []).map(q => ({
        text: typeof q === 'object' ? (q.text || q.question || '') : String(q),
        type: "query",
        compiler: null,          // no lock — candidate selects language freely
        followUpCount: 0,
        visibleTestCases: [],    // query questions have no test cases
      }));
      
      if (queryQs.length > 0) {
        normalized.push(...queryQs);
      }

      if (!normalized.length) {
        setError("No questions received");
        return;
      }

      // ── Inject architecture question if eligible (not for AI_TECHNICAL — verbal only) ──
      const archQText = getArchQuestion(response.data.primarySkills || context?.primarySkills, response.data.level || context?.level);
      const resolvedInterviewType = response.data.interviewType || context?.interviewType;
      if (archQText && resolvedInterviewType !== 'AI_TECHNICAL') {
        normalized.push({
          text: archQText,
          type: "architecture",
          compiler: null,
          topic: "System Design",
          followUpCount: 0
        });
      }

      setQuestions(normalized);

      setInterviewStarted(true);
      startNetworkMonitoring();
      setStage("questioning");
      setCurrentQuestionIndex(0);

      const interviewDuration = context?.timeLimit || 30;

      startTimer(interviewDuration);
      timeLeftRef.current = interviewDuration * 60;

      // ✅ SAFE SPEAK
      speak(normalized[0]);



    } catch (err) {
      const serverData = err.response?.data;
      console.error("START ERROR:", serverData || err.message);
      const displayMsg = serverData?.reason || serverData?.message || err.message || "Failed to start interview. Please check your connection and try again.";
      setError(displayMsg);
    }
  };


  const speak = (input) => {
    if (!synthRef) return;

    // 🔥 FORCE STRING
    let text = "";

    if (typeof input.text === "string") {
      text = input.text;
    } else if (typeof input.question === "string") {
      text = input.question;
    } else {
      text = JSON.stringify(input);
    }

    if (!text) {
      console.error("Invalid speak input:", input);
      return;
    }

    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch {}
      recognitionRef.current = null;   // 🔥 ADD THIS LINE
      setIsListening(false);
    }

    synthRef.cancel();

    const utterance = new SpeechSynthesisUtterance(text);

    utterance.onstart = () => {
      aiSpeakingRef.current = true;
      setIsAISpeaking(true);

      // 🔥 HARD STOP MIC
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch {}
        recognitionRef.current = null;   // 🔥 IMPORTANT
      }

      setIsListening(false);
      setShowNextButton(false);
      pauseDurationsRef.current = [];
      lastSpeechTimeRef.current = null;

      setTranscript("");
      finalTranscriptRef.current = "";
    };

    utterance.onend = () => {
      aiSpeakingRef.current = false;
      setIsAISpeaking(false);
      setShowNextButton(true);
      setQuestionStartTime(Date.now());

      // Architecture questions: candidate draws, no speech needed
      if (input?.type !== "architecture") {
        startListening();
      }
    };

    setAiResponse(text);
    synthRef.speak(utterance);
  };
  const startListening = () => {
    // 🔥 HARD RESET (VERY IMPORTANT)
    if (recognitionRef.current) {
      try {
        recognitionRef.current.onend = null;
        recognitionRef.current.onerror = null;
        recognitionRef.current.onresult = null;
        recognitionRef.current.stop();
      } catch {}
    }

    recognitionRef.current = null;

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setError('Your browser does not support Speech Recognition. Please use Chrome.');
      return;
    }



    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;

    recognition.lang = 'en-US';
    recognition.interimResults = true;
    recognition.continuous = true;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      console.log("🎤 MIC STARTED");
      setIsListening(true);
    };

    recognition.onerror = (event) => {
      console.error("Speech error:", event.error);

      // 🔥 AUTO RECOVER
      setTimeout(() => {
        if (!aiSpeakingRef.current) {
          startListening();
        }
      }, 800);
    };



    recognition.onresult = (event) => {
      resetIdleTimer();
      const now = Date.now();

      // ONLY track when FINAL speech chunk ends
      const isFinal = event.results[event.results.length - 1].isFinal;

      if (isFinal) {
        if (lastSpeechTimeRef.current) {
          const gap = now - lastSpeechTimeRef.current;

          if (gap > 2000 && gap < 8000) {  // ignore extreme noise
            pauseDurationsRef.current.push(gap);
          }
        }

        lastSpeechTimeRef.current = now;
      }
      // Ignore anything while AI is speaking
      if (aiSpeakingRef.current) return;
      let interimTranscript = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {

        const text = event.results[i][0].transcript;

        if (event.results[i].isFinal) {
          finalTranscriptRef.current += text + " ";
        } else {
          interimTranscript += text;
        }

      }

      setTranscript(finalTranscriptRef.current + interimTranscript);

    };

    recognition.onend = () => {
      setIsListening(false);

      // 🔥 DO NOT restart if AI is speaking
      if (aiSpeakingRef.current) return;

      if (interviewStarted && stage === "questioning" && !aiSpeakingRef.current) {
        setTimeout(() => {
          startListening(); // 🔥 NEW INSTANCE instead of reuse
        }, 400);
      }
    };

    recognition.start();
  };

  const handleNextQuestion = async () => {
    if (loadingNext) return;
    setLoadingNext(true);

    try {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
        setIsListening(false);
      }
      setShowNextButton(false);

      // Save current answer immediately
      const currentQ = questions[currentQuestionIndex];

      if (currentQ?.type === "architecture") {
        // Save architecture canvas answer
        const archAnswer = {
          question: currentQ?.text,
          answer: "Architecture drawing submitted",
          questionType: "architecture",
          canvasData: archCanvasData,
          timeTaken: Math.floor((Date.now() - questionStartTime) / 1000)
        };
        setAnswers(prev => [...prev, archAnswer]);
        totalResponsesRef.current += 1;
        finalTranscriptRef.current = "";
        setTranscript('');
      } else if (currentQ?.type !== "coding" && currentQ?.type !== "query") {
        const currentAnswer = {
          question: currentQ?.text,
          answer: transcript.trim() || "No response",
          code: null,
          language: null,
          timeTaken: Math.floor((Date.now() - questionStartTime) / 1000)
        };
        const updatedAnswers = [...answers, currentAnswer];
        setAnswers(updatedAnswers);
        if (currentAnswer.answer && currentAnswer.answer !== "No response") {
          totalResponsesRef.current += 1;
        }
      } else {
        // Coding/SQL/frontend question: snapshot the current editor state before
        // clearing it so the answer is available at submit time even without
        // a manual "Save Answer" click.
        const isFrontendNext = lockedCompiler === 'frontend';
        const codeForSnapshot = isFrontendNext
          ? `<!-- HTML -->\n${htmlCode}\n\n/* CSS */\n${cssCode}`
          : code;
        const langForSnapshot = isFrontendNext
          ? 'HTML/CSS'
          : (SUPPORTED_LANGUAGES.find(l => l.value === language)?.label || language || '');

        if (codeForSnapshot && codeForSnapshot.trim()) {
          questionEditorSnapshotsRef.current[currentQuestionIndex] = {
            ...(questionEditorSnapshotsRef.current[currentQuestionIndex] || {}),
            question:        currentQ.text,
            questionIndex:   currentQuestionIndex,
            codeContent:     codeForSnapshot,
            selectedLanguage: langForSnapshot,
            htmlCode,
            cssCode,
            lockedCompiler,
            consoleLogs:     consoleLogs || [],
            // Execution metadata already captured by the lastExecutionMetadata useEffect
          };
        }

        if (answers.some(a =>
          a.questionIndex === currentQuestionIndex || a.question === currentQ.text
        )) {
          totalResponsesRef.current += 1;
        }
      }

      setCode("");

      const updatedAnswers = [...answers]; // use current answers which might include the saved coding answer

      const isLastQuestion =
        currentQuestionIndex >= questions.length - 1;

      if (!isLastQuestion) {
        // ✅ Move to next scheduled question (NO AI CALL)
        setCurrentQuestionIndex(prev => prev + 1);

        finalTranscriptRef.current = "";
        setTranscript('');

        speak(questions[currentQuestionIndex + 1]);

        setLoadingNext(false);
        return; // 🚨 CRITICAL
      }

      // 🔥 ALWAYS CALL BACKEND FOR FOLLOW-UP
      if (timeLeftRef.current > 0) {
      try {
        const res = await axios.post(
          `${API_BASE_URL}/interviewRoutes/ai-interview/more-questions`,
          {
            jobTitle: context.jobTitle,
            resume: context.resumeUrl,
            previousQuestions: questions,
            answers: updatedAnswers, // 🔥 CRITICAL
            depth: questions[questions.length - 1]?.depth || 0,
            primarySkills: context.primarySkills,
            level: context.level,
            interviewType: context.interviewType,
            screenResolution: getScreenResolution(),
          }
        );

        const newQRaw = res.data.questions?.[0];

        if (!newQRaw) throw new Error("No follow-up question");

        const level = context.level?.toLowerCase();

        let questionText =
          newQRaw.text ||
          newQRaw.question ||
          newQRaw?.Text?.Question ||
          "Invalid question";

        // 🔥 LEVEL ENFORCEMENT
        if (level === "fresher") {
          questionText = questionText
            .replace(/trade-offs|optimize|scalability|edge cases/gi, "basic concept")
            .replace(/design|architecture/gi, "simple explanation");
        }

        const rawQType = (newQRaw.type || "normal").toLowerCase().trim();
        const newQ = {
          text: questionText,
          type: (context?.interviewType === 'AI_TECHNICAL' && rawQType === 'coding') ? 'normal' : rawQType,
          topic: newQRaw.topic,
          followUpCount: newQRaw.followUpCount,
          depth: newQRaw.depth || (questions[questions.length - 1]?.depth || 0) + 1
        };

        setQuestions(prev => [...prev, newQ]);
        setCurrentQuestionIndex(prev => prev + 1);

        finalTranscriptRef.current = "";
        setTranscript('');

        speak(newQ);

      } catch (err) {
        console.error("Follow-up failed:", err);

        // 🔥 LEVEL-AWARE FALLBACK (REPLACE FULL BLOCK)
        const getLevelBasedFallback = () => {
          const skill = context.primarySkills?.split(",")[0] || "this topic";
          const level = context.level?.toLowerCase();

          if (level === "fresher") {
            return `What is ${skill}? Explain with a simple example.`;
          }

          if (level === "mid") {
            return `How is ${skill} used in real-world applications? Give an example.`;
          }

          if (level === "experienced") {
            return `In real-world systems, how is ${skill} used? Explain trade-offs, scalability challenges, and edge cases.`;
          }

          return `Explain ${skill} with an example.`;
        };

        const fallback = {
          text: getLevelBasedFallback(),
          type: "normal"
        };

        setQuestions(prev => [...prev, fallback]);
        setCurrentQuestionIndex(prev => prev + 1);

        finalTranscriptRef.current = "";
        setTranscript('');

        speak(fallback);
      }
      } else {
        // do nothing — wait for timer to end naturally
        return;
      }
    } finally {
      setLoadingNext(false);
    }
  };

 const handleSubmitInterview = async (terminated = false) => {
   // 1. Safety Lock
   if (isSubmittingRef.current) return;
   isSubmittingRef.current = true;

   setIsSubmitting(true);
   setLoading(true);

   const pauseCount = pauseDurationsRef.current.length;

   const avgPause =
     pauseCount > 0
       ? Math.round(
           pauseDurationsRef.current.reduce((a, b) => a + b, 0) /
           pauseCount
         ) // 🔥 KEEP IN ms (NO /1000)
       : 0;

   let effectivePauseCount = pauseCount;
   let effectiveAvgPause = avgPause;

   const hasAnySpeech =
     answers.some(a => a.answer?.trim()) || transcript.trim();

   if (!hasAnySpeech) {
     effectivePauseCount = -1;
     effectiveAvgPause = -1;
   }

   try {
     // 2. THE FIX: Grab current transcript if it's not in the 'answers' array yet
     let currentAnswers = [...answers];

     // If there is text in the box that wasn't "saved" by a Next button click
     const currentQ = questions[currentQuestionIndex];
     if (transcript.trim() || ((currentQ?.type === 'coding' || currentQ?.type === 'query') && code.trim())) {
       const isCoding = currentQ?.type === 'coding' || currentQ?.type === 'query' || currentQ?.text?.toLowerCase().includes('coding');
      
       const newAnswer = {
         question: currentQ?.text,
         timeTaken: Math.floor((Date.now() - questionStartTime) / 1000)
       };

       if (isCoding) {
         // Issue 1: for frontend (HTML/CSS) questions capture rendered preview output.
         const isFrontendSubmit = lockedCompiler === "frontend" || currentQ?.compiler === "frontend";
         const frontendSubmitCode = isFrontendSubmit
           ? `<!-- HTML -->\n${htmlCode}\n\n/* CSS */\n${cssCode}`
           : code;
         const frontendSubmitLang = isFrontendSubmit
           ? "HTML/CSS"
           : (SUPPORTED_LANGUAGES.find(l => l.value === language)?.label || language);
         const isFrontendCode2 = isFrontendSubmit && (htmlCode.trim().length > 0 || cssCode.trim().length > 0);
         let frontendSubmitRendered = null;
         let frontendSubmitSnapshot = null;
         if (isFrontendSubmit && previewIframeRef.current) {
           try {
             frontendSubmitRendered = previewIframeRef.current.contentDocument?.body?.innerText?.trim() || null;
             // Capture the full HTML content of the iframe for report rendering
             frontendSubmitSnapshot = previewIframeRef.current.contentDocument?.documentElement?.outerHTML || null;
           } catch { /* sandbox cross-origin fallback */ }
         }
         const frontendSubmitConsolePart = consoleLogs.length > 0
           ? consoleLogs.map(l => `[${l.level.toUpperCase()}] ${l.message}`).join('\n')
           : null;
         const frontendSubmitOutput = isFrontendSubmit
           ? ([
               frontendSubmitRendered    ? `=== Rendered Preview ===\n${frontendSubmitRendered}` : null,
               frontendSubmitConsolePart ? `=== Console Logs ===\n${frontendSubmitConsolePart}` : null,
             ].filter(Boolean).join('\n\n') || (isFrontendCode2 ? `=== Rendered Preview ===\n${htmlCode}` : null))
           : null;

         newAnswer.answer                = frontendSubmitCode || "No code written";
         newAnswer.questionType          = "coding";
         newAnswer.selectedLanguage      = frontendSubmitLang;
         newAnswer.sourceCode            = frontendSubmitCode;
         newAnswer.frontendSnapshot      = frontendSubmitSnapshot;
         newAnswer.executionOutput       = frontendSubmitOutput || lastExecutionMetadata?.executionOutput    || null;
         newAnswer.compileError          = lastExecutionMetadata?.compileError           || null;
         newAnswer.runtimeError          = lastExecutionMetadata?.runtimeError           || null;
         newAnswer.terminalOutput        = frontendSubmitOutput || lastExecutionMetadata?.terminalOutput     || null;
         newAnswer.executionStatus       = isFrontendSubmit
           ? (isFrontendCode2 ? "success" : "not_executed")
           : (lastExecutionMetadata?.executionStatus        || "not_executed");
         newAnswer.executedAt            = isFrontendSubmit
           ? (isFrontendCode2 ? new Date() : null)
           : (lastExecutionMetadata?.executedAt             || null);
         newAnswer.testCaseResults = lastExecutionMetadata?.testCaseResults || testCaseResults;
         newAnswer.testCaseSummary       = lastExecutionMetadata?.testCaseSummary        || null;
         newAnswer.minimumPassThreshold  = questions[currentQuestionIndex]?.minimumPassThreshold || 2;
         newAnswer.consoleLogs           = consoleLogs;
         // SQL hidden test case results for scoring
         if (lastExecutionMetadata?.sqlHiddenTestResults?.length) {
           newAnswer.sqlHiddenTestResults = lastExecutionMetadata.sqlHiddenTestResults;
         }
         if (lastExecutionMetadata?.sqlTierScore !== null && lastExecutionMetadata?.sqlTierScore !== undefined) {
           newAnswer.sqlTierScore = lastExecutionMetadata.sqlTierScore;
         }
         if (lastExecutionMetadata?.sqlFeedback?.length) {
           newAnswer.sqlFeedback = lastExecutionMetadata.sqlFeedback;
         }
       } else {
         newAnswer.answer = transcript.trim();
       }

       currentAnswers.push(newAnswer);
       totalResponsesRef.current += 1;
     }

     // ── Merge auto-saved coding answers that were never manually saved ──────
     // For every coding/SQL/frontend question the candidate visited, check if
     // currentAnswers already has an entry. If not, pull the latest editor
     // snapshot (captured every time code changed) so no work is ever lost.
     {
       const answeredIdxSet = new Set(
         currentAnswers
           .filter(a => a.questionIndex !== undefined)
           .map(a => Number(a.questionIndex))
       );

       for (const [idxStr, snap] of Object.entries(questionEditorSnapshotsRef.current)) {
         const idx = parseInt(idxStr, 10);
         if (answeredIdxSet.has(idx)) continue; // already have an answer for this question

         const snapQ = questions[idx];
         if (!snapQ || !snap.codeContent || !snap.codeContent.trim()) continue;

         const meta = snap.executionMeta || null;
         currentAnswers.push({
           question:              snapQ.text,
           questionIndex:         idx,
           answer:                snap.codeContent,
           questionType:          'coding',
           selectedLanguage:      snap.selectedLanguage || '',
           sourceCode:            snap.codeContent,
           frontendSnapshot:      null,
           executionOutput:       meta?.executionOutput    || null,
           compileError:          meta?.compileError       || null,
           runtimeError:          meta?.runtimeError       || null,
           terminalOutput:        meta?.terminalOutput     || null,
           executionStatus:       meta?.executionStatus    || 'not_executed',
           executedAt:            meta?.executedAt         || null,
           testCaseResults:       meta?.testCaseResults    || [],
           testCaseSummary:       meta?.testCaseSummary    || null,
           minimumPassThreshold:  snapQ.minimumPassThreshold || 2,
           consoleLogs:           snap.consoleLogs         || [],
           timeTaken:             0,
           answerSource:          'autosave',
           ...(meta?.schemaAutoLoaded !== undefined && { schemaAutoLoaded: meta.schemaAutoLoaded }),
           ...(meta?.sqlHiddenTestResults?.length && { sqlHiddenTestResults: meta.sqlHiddenTestResults }),
           ...(meta?.sqlTierScore !== null && meta?.sqlTierScore !== undefined && { sqlTierScore: meta.sqlTierScore }),
           ...(meta?.sqlFeedback?.length && { sqlFeedback: meta.sqlFeedback }),
         });

         console.log(`[AutoSave] Included auto-saved answer for Q${idx + 1} — source: snapshot, language: ${snap.selectedLanguage}`);
       }
     }
     // ────────────────────────────────────────────────────────────────────────

     let avgResponseTime = 0;

     if (currentAnswers.length > 0){
       avgResponseTime = Math.round(
         currentAnswers.reduce((sum, a) => sum + (a.timeTaken || 0), 0) / currentAnswers.length
       );
     } else {
       avgResponseTime = 999; // no response
     }


     // 3. Prepare payload
     const payload = {
       answers: currentAnswers, // Use the local variable we just built
       violations: violationsRef.current,
       interviewType: context.interviewType,
       events: eventsRef.current.slice(-50),
       behavior_metrics: {
         responseTime: avgResponseTime,
         pauseCount: effectivePauseCount,
         avgPause: effectiveAvgPause,
         correctionRate: 0,
         idleWarnings: idleWarningsRef.current,
         totalResponses: totalResponsesRef.current
       }
     };

     // Include architecture canvas data if available
     const latestArchData = archCanvasData || (() => {
       const archAns = currentAnswers.find(a => a.questionType === 'architecture');
       return archAns?.canvasData || null;
     })();
     if (latestArchData) {
       payload.architecture_canvas = {
         shapes: latestArchData.shapes || [],
         canvasImage: latestArchData.canvasImage || null,
         question: latestArchData.question || '',
         savedAt: latestArchData.savedAt || new Date().toISOString(),
         shapeCount: (latestArchData.shapes || []).length,
       };
     }

     const url = isHrInterviewSession
       ? (terminated
           ? `${API_BASE_URL}/interviewRoutes/hr-interview/terminate/${sessionToken}`
           : `${API_BASE_URL}/interviewRoutes/hr-interview/submit/${sessionToken}`)
       : isTechInterviewSession
       ? (terminated
           ? `${API_BASE_URL}/interviewRoutes/technical-interview/terminate/${sessionToken}`
           : `${API_BASE_URL}/interviewRoutes/technical-interview/submit/${sessionToken}`)
       : (terminated
           ? `${API_BASE_URL}/interviewRoutes/ai-interview/terminate/${interviewId}`
           : `${API_BASE_URL}/interviewRoutes/ai-interview/submit/${interviewId}`);

     if (!terminated) {
       payload.isTerminated = false;
     }

     // 4. Send to Backend
     const submitWithRetry = async (payload, retries = 3) => {
       try {
         return await axios.post(url, payload, {
           withCredentials: true
         });
       } catch (err) {
         if (retries > 0) {
           await new Promise(r => setTimeout(r, 1000));
           return submitWithRetry(payload, retries - 1);
         }
         throw err;
       }
     };

     await submitWithRetry(payload);

     // Clear draft after successful submission so it doesn't restore on next visit
     clearDraft().catch(() => {});
     if (autoSaveIntervalRef.current) clearInterval(autoSaveIntervalRef.current);

     setTimeout(() => {
       navigate("/candidate/dashboard");
     }, 1500);

   } catch (err) {
     console.error("Submit Error:", err);
     if (!terminated) {
       isSubmittingRef.current = false;
       setIsSubmitting(false);
       setLoading(false);
     }
   }
 };
  useEffect(() => {
    const handleUnload = () => {
      stopNetworkMonitoring();
      if (!interviewStarted || isTerminated || isSubmittingRef.current) {
        return;
      }

      // Merge any snapshot-only coding answers into the answers array for this unload
      const snapshotAnswersForUnload = (() => {
        const existingIdxSet = new Set(answers.filter(a => a.questionIndex !== undefined).map(a => Number(a.questionIndex)));
        const merged = [];
        for (const [idxStr, snap] of Object.entries(questionEditorSnapshotsRef.current)) {
          const idx = parseInt(idxStr, 10);
          if (existingIdxSet.has(idx) || !snap.codeContent?.trim()) continue;
          const snapQ = questions[idx];
          if (!snapQ) continue;
          const meta = snap.executionMeta || null;
          merged.push({
            question: snapQ.text, questionIndex: idx, answer: snap.codeContent,
            questionType: 'coding', selectedLanguage: snap.selectedLanguage || '',
            sourceCode: snap.codeContent, executionOutput: meta?.executionOutput || null,
            compileError: meta?.compileError || null, runtimeError: meta?.runtimeError || null,
            executionStatus: meta?.executionStatus || 'not_executed',
            testCaseResults: meta?.testCaseResults || [], testCaseSummary: meta?.testCaseSummary || null,
            minimumPassThreshold: snapQ.minimumPassThreshold || 2, consoleLogs: snap.consoleLogs || [],
            timeTaken: 0, answerSource: 'autosave',
            ...(meta?.sqlHiddenTestResults?.length && { sqlHiddenTestResults: meta.sqlHiddenTestResults }),
            ...(meta?.sqlTierScore !== null && meta?.sqlTierScore !== undefined && { sqlTierScore: meta.sqlTierScore }),
            ...(meta?.sqlFeedback?.length && { sqlFeedback: meta.sqlFeedback }),
          });
        }
        return merged;
      })();
      const allAnswersForUnload = [
        ...answers,
        ...snapshotAnswersForUnload,
        ...(transcript.trim() ? [{ question: questions[currentQuestionIndex]?.text, answer: transcript.trim() }] : []),
      ];

      // Save current draft state via sendBeacon (survives page unload reliably)
      if (draftId) {
        const draftPayload = JSON.stringify({
          interviewId: draftId,
          draft: {
            candidateCode: code,
            selectedLanguage: language,
            htmlCode,
            cssCode,
            questionIndex: currentQuestionIndex,
            lockedCompiler,
            answers: allAnswersForUnload,
          },
          saveSource: 'unload',
        });
        try {
          navigator.sendBeacon(
            `${API_BASE_URL}/interviewRoutes/save-draft`,
            new Blob([draftPayload], { type: 'application/json' })
          );
        } catch { /* sendBeacon not supported — already saved to localStorage above */ }
        // Also persist to localStorage synchronously as backup
        try {
          localStorage.setItem(draftLocalKey, JSON.stringify({
            candidateCode: code, selectedLanguage: language,
            htmlCode, cssCode, questionIndex: currentQuestionIndex,
            lockedCompiler, answers, savedAt: Date.now(),
          }));
        } catch { /* storage full */ }
      }

      const url = isHrInterviewSession
        ? `${API_BASE_URL}/interviewRoutes/hr-interview/terminate/${sessionToken}`
        : isTechInterviewSession
        ? `${API_BASE_URL}/interviewRoutes/technical-interview/terminate/${sessionToken}`
        : `${API_BASE_URL}/interviewRoutes/ai-interview/terminate/${interviewId}`;
      const body = JSON.stringify({
        interviewType: context.interviewType,
        answers: allAnswersForUnload,
        violations: violationsRef.current,
        events: eventsRef.current.slice(-50),
        screenResolution: getScreenResolution(),
        behavior_metrics: {
          responseTime: 0,
          pauseCount: pauseDurationsRef.current.length,
          avgPause: 0,
          correctionRate: 0,
          idleWarnings: idleWarningsRef.current,
          totalResponses: totalResponsesRef.current
        }
      });
      const blob = new Blob([body], { type: "application/json" });
      fetch(url, {
        method: "POST",
        body: blob,
        keepalive: true, // 🔥 Added keepalive for better reliability on tab close
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "include"
      });
    };

    window.addEventListener("beforeunload", handleUnload);
    window.addEventListener("unload", handleUnload); // Added unload for extra safety

    return () => {
      stopNetworkMonitoring();
      window.removeEventListener("beforeunload", handleUnload);
      window.removeEventListener("unload", handleUnload);
    };
  }, [interviewStarted, isTerminated, interviewId, answers, transcript, questions, currentQuestionIndex]);

  if (error === "completed") {
    return (
      <div style={{ textAlign: "center", marginTop: "100px" }}>
        <h2>Interview Already Completed</h2>
        <button onClick={() => navigate("/candidate-dashboard")}>
          Go Back
        </button>
      </div>
    );
  }

  if (error === "terminated") {
    return (
      <div style={{ textAlign: "center", marginTop: "100px" }}>
        <h2>Interview Already Terminated</h2>
        <button onClick={() => navigate("/candidate/dashboard")}>
          Go Back
        </button>
      </div>
    );
  }

  if (error === "expired") {
    return (
      <div
        style={{
          height: "100%",
          background: "white",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          fontSize: "24px",
          gap: "20px"
        }}
      >
        <div>Your interview got expired</div>

        <button
          onClick={() => navigate("/candidate/dashboard")}
          style={{
            padding: "10px 20px",
            fontSize: "16px",
            backgroundColor: "#4F46E5",
            color: "white",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer"
          }}
        >
          Return to Dashboard
        </button>
      </div>
    );
  }



  if (loading && !context) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  if (isTerminated) return (
      <Container sx={{ py: 6, textAlign: "center" }}>
        <Paper sx={{ p: 6, borderRadius: "0 !important" }}>
          <Typography variant="h4" color="error">Interview Terminated</Typography>
          <Typography sx={{ my: 3 }}>Security violations detected.</Typography>
          <Button variant="contained" onClick={() => navigate("/candidate/dashboard")}>Return to Dashboard</Button>
        </Paper>
      </Container>
  );



  if (!interviewStarted && !error) {
    const instructionItems = [
      {
        icon: <AssignmentOutlined sx={{ color: "#312e81" }} />,
        title: "You & Your Environment",
        desc: "Ensure you are in a quiet, well-lit place. Only you should be present in the room."
      },
      {
        icon: <VideocamOutlined sx={{ color: "#312e81" }} />,
        title: "Camera Must Stay On",
        desc: "Keep your face clearly visible at all times. Do not cover your face or leave the camera frame."
      },
      {
        icon: <MicNoneOutlined sx={{ color: "#312e81" }} />,
        title: "Microphone Must Stay On",
        desc: "Speak clearly and ensure your microphone is working throughout the interview."
      },
      {
        icon: <VisibilityOutlined sx={{ color: "#312e81" }} />,
        title: "Stay Attentive",
        desc: "Do not look away for long periods. Stay focused on the screen."
      },
      {
        icon: <TabUnselectedOutlined sx={{ color: "#312e81" }} />,
        title: "Do Not Leave the Screen",
        desc: "Do not switch tabs, minimize the window, or leave the interview page."
      },
      {
        icon: <PhonelinkOffOutlined sx={{ color: "#312e81" }} />,
        title: "VPN / Network",
        desc: "Do not change your VPN or network during the interview."
      },
      {
        icon: <VpnLockOutlined sx={{ color: "#312e81" }} />,
        title: "No External Help",
        desc: "Do not use any external devices, notes, or assistance."
      },
      {
        icon: <DoDisturbOnOutlined sx={{ color: "#312e81" }} />,
        title: "Important",
        desc: "Violations may be detected and the interview may be terminated."
      }
    ];

    const monitorItems = [
      {
        icon: <CenterFocusStrongOutlined sx={{ color: "#312e81", fontSize: 32 }} />,
        title: "Face Detection",
        desc: "Your face must be visible at all times."
      },
      {
        icon: <RemoveRedEyeOutlined sx={{ color: "#312e81", fontSize: 32 }} />,
        title: "Eye Gaze",
        desc: "Detects if you are looking away for too long."
      },
      {
        icon: <ScreenRotationOutlined sx={{ color: "#312e81", fontSize: 32 }} />,
        title: "Head Movement",
        desc: "Detects excessive head turning."
      },
      {
        icon: <TabUnselectedOutlined sx={{ color: "#312e81", fontSize: 32 }} />,
        title: "Leaving Screen",
        desc: "Detects tab switching, minimizing or leaving the window."
      },
      {
        icon: <VpnLockOutlined sx={{ color: "#312e81", fontSize: 32 }} />,
        title: "VPN / Network Change",
        desc: "Detects VPN or network changes during the interview."
      },
      {
        icon: <GroupsOutlined sx={{ color: "#312e81", fontSize: 32 }} />,
        title: "Multiple People",
        desc: "Detects other people entering the frame."
      }
    ];

    return (
        <Box sx={{ bgcolor: "#f5f7f9", minHeight: "100vh", p: { xs: 2, md: 4 }, borderRadius: "0 !important" }}>
        <Container maxWidth="lg" sx={{ p: 0 }}>
          {/* Header */}
          <Paper
            elevation={0}
            sx={{
              bgcolor: "#312e81",
              color: "white",
              p: 3,
              borderRadius: "0 !important",
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box sx={{
                width: 50,
                height: 50,
                borderRadius: "0 !important",
                border: "2px solid rgba(255,255,255,0.8)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}>
                <Mic sx={{ fontSize: 30 }} />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight="bold" sx={{ color: "#FFFFFF" }}>{getInterviewTitle()}</Typography>
                <Typography variant="body2" sx={{ color: "white", opacity: 0.9 }}>
                  Please read all instructions carefully before starting your interview.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 3, mt: { xs: 2, md: 0 } }}>
              <Box sx={{
                bgcolor: "rgba(0,0,0,0.2)",
                p: 1.5,
                borderRadius: "0 !important",
                textAlign: "center",
                minWidth: "120px",
                border: "1px solid rgba(255,255,255,0.1)"
              }}>
                <Typography variant="caption" sx={{ display: "block", fontSize: "10px", fontWeight: "bold", mb: 0.5, borderRadius: "0 !important" }}>
                  <Box component="span" sx={{ mr: 0.5, borderRadius: "0 !important" }}>📅</Box> STARTS IN
                </Typography>
                <Typography variant="h4" fontWeight="bold" sx={{ lineHeight: 1, borderRadius: "0 !important" }}>
                  {preStartTimeLeft !== null ? formatTime(preStartTimeLeft).split(':')[0] : "00"}
                  <Box component="span" sx={{ fontSize: "20px", mx: 0.5, borderRadius: "0 !important" }}>:</Box>
                  {preStartTimeLeft !== null ? formatTime(preStartTimeLeft).split(':')[1] : "00"}
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "space-between", px: 1, mt: 0.5, borderRadius: "0 !important" }}>
                   <Typography variant="caption" sx={{ fontSize: "9px", borderRadius: "0 !important" }}>MINUTES</Typography>
                   <Typography variant="caption" sx={{ fontSize: "9px", borderRadius: "0 !important" }}>SECONDS</Typography>
                </Box>
              </Box>

              <Box sx={{
                p: 1.5,
                textAlign: "center",
                minWidth: "120px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                borderLeft: "1px solid rgba(255,255,255,0.3)",
                borderRadius: "0 !important"
              }}>
                <Typography variant="caption" sx={{ display: "block", fontSize: "10px", fontWeight: "bold", mb: 0.5, borderRadius: "0 !important" }}>
                  <Box component="span" sx={{ mr: 0.5, borderRadius: "0 !important" }}>🕒</Box> TOTAL INTERVIEW TIME
                </Typography>
                <Typography variant="h4" fontWeight="bold" sx={{ lineHeight: 1, borderRadius: "0 !important" }}>
                  {(context?.timeLimit || 30)}:00
                </Typography>
                <Typography variant="caption" sx={{ fontSize: "9px", mt: 0.5, borderRadius: "0 !important" }}>MINUTES</Typography>
              </Box>
            </Box>
          </Paper>

          <Grid container spacing={3}>
            {/* Left Column - Instructions */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, borderRadius: "0 !important", height: "100%" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
                  <AssignmentOutlined sx={{ color: "#312e81" }} />
                  <Typography variant="h6" fontWeight="bold">Important Instructions</Typography>
                </Box>

                <Box>
                  {instructionItems.map((item, idx) => (
                    <Box key={idx} sx={{
                      display: "flex",
                      gap: 2,
                      mb: idx === instructionItems.length - 1 ? 0 : 2,
                      pb: idx === instructionItems.length - 1 ? 0 : 2,
                      borderBottom: idx === instructionItems.length - 1 ? "none" : "1px solid #f0f0f0",
                      borderRadius: "0 !important"
                    }}>
                      <Box sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "0 !important",
                        bgcolor: "#eef2ff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0
                      }}>
                        {item.icon}
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ color: "#333" }}>{item.title}</Typography>
                        <Typography variant="caption" sx={{ color: "#666", display: "block", mt: 0.5 }}>{item.desc}</Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>

                <Box sx={{
                  mt: 3,
                  p: 1.5,
                  bgcolor: "#eef2ff",
                  borderRadius: "0 !important",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  border: "1px solid #c7d2fe"
                }}>
                  <Box
                    onClick={() => setAgreed(!agreed)}
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: "0 !important",
                      border: "2px solid #312e81",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      bgcolor: agreed ? "#312e81" : "transparent"
                    }}
                  >
                    {agreed && <Check sx={{ color: "white", fontSize: 16, borderRadius: "0 !important" }} />}
                  </Box>
                  <Typography variant="caption" sx={{ color: "#1e1b4b", fontWeight: 500 }}>
                    I have read and understood all the instructions above. I agree to follow the guidelines.
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* Right Column - Monitoring */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, borderRadius: "0 !important", height: "100%" }}>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>What We Monitor</Typography>
                <Typography variant="caption" sx={{ color: "#666", display: "block", mb: 3 }}>
                  Our system monitors the following to ensure a fair assessment.
                </Typography>

                <Grid container spacing={2}>
                  {monitorItems.map((item, idx) => (
                    <Grid item xs={6} key={idx}>
                      <Box sx={{
                        p: 2,
                        borderRadius: "0 !important",
                        border: "1px solid #f0f0f0",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        gap: 1
                      }}>
                        {item.icon}
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ color: "#333", borderRadius: "0 !important" }}>{item.title}</Typography>
                        <Typography variant="caption" sx={{ color: "#666", lineHeight: 1.2, borderRadius: "0 !important" }}>{item.desc}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                <Box sx={{
                  mt: 3,
                  p: 2,
                  bgcolor: "#f0f7ff",
                  borderRadius: "0 !important",
                  display: "flex",
                  gap: 2,
                  border: "1px solid #e1f0ff"
                }}>
                  <SecurityOutlined sx={{ color: "#1976d2" }} />
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ color: "#1565c0" }}>Why This Matters</Typography>
                    <Typography variant="caption" sx={{ color: "#1e88e5", display: "block", mt: 0.5, lineHeight: 1.3 }}>
                      These measures help us maintain the integrity of the interview process and ensure a fair experience for all candidates.
                    </Typography>
                  </Box>
                </Box>

                <Paper sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: "0 !important",
                  bgcolor: "#fff8e1",
                  border: "1px solid #ffe082",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  width: "100%"
                }}>
                  <WarningAmberOutlined sx={{ color: "#f57c00" }} />
                  <Typography variant="caption" sx={{ color: "#e65100", fontWeight: 500 }}>
                    Please do not attempt to bypass these checks. Any suspicious activity may lead to disqualification.
                  </Typography>
                </Paper>
              </Paper>
            </Grid>
          </Grid>

          {/* Bottom Button and Warning */}
          <Box sx={{ mt: 3, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>

            {/* Screen share — REQUIRED; must be entire screen, not a tab or window */}
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.75 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Button
                  variant={randomCaptureControls.screenShareIsMonitor ? "contained" : "outlined"}
                  onClick={handleRequiredScreenShare}
                  disabled={randomCaptureControls.screenShareIsMonitor}
                  size="small"
                  sx={{
                    borderColor: randomCaptureControls.screenShareIsMonitor ? "#312e81" : "#c62828",
                    color:       randomCaptureControls.screenShareIsMonitor ? "#fff"    : "#c62828",
                    bgcolor:     randomCaptureControls.screenShareIsMonitor ? "#312e81 !important" : "transparent",
                    textTransform: "none",
                    fontSize: "0.8rem",
                    px: 2,
                  }}
                >
                  {randomCaptureControls.screenShareIsMonitor
                    ? "✓ Entire Screen Sharing Active"
                    : "Enable Entire Screen Sharing (Required)"}
                </Button>
                <Typography variant="caption" sx={{ color: randomCaptureControls.screenShareIsMonitor ? "#2e7d32" : "#c62828", fontWeight: 500 }}>
                  {randomCaptureControls.screenShareIsMonitor ? "Screen monitoring active" : "Required to start interview"}
                </Typography>
              </Box>
              {screenShareError && (
                <Typography variant="caption" sx={{ color: "#c62828", maxWidth: 480, textAlign: "center" }}>
                  {screenShareError}
                </Typography>
              )}
              {!randomCaptureControls.screenShareIsMonitor && !screenShareError && (
                <Typography variant="caption" sx={{ color: "#888", maxWidth: 480, textAlign: "center" }}>
                  When prompted, select "Entire Screen" — browser tabs and windows will be rejected.
                </Typography>
              )}
            </Box>

            <Button
              variant="contained"
              disabled={loading || isCameraInitializing || !agreed || !randomCaptureControls.screenShareIsMonitor || (preStartTimeLeft !== 0 && preStartTimeLeft !== null)}
              onClick={async () => {
                if (error === "terminated") return;
                setInterviewStarted(true);
                setStage("questioning");
                await startStream();
                setTimeout(() => {
                  startInterview();
                }, 300);
              }}
              sx={{
                bgcolor: "#312e81 !important",
                color: "white",
                px: 6,
                py: 1.5,
                borderRadius: "0 !important",
                textTransform: "none",
                fontSize: "1.1rem",
                fontWeight: "bold",
                boxShadow: "0 4px 12px rgba(0,105,92,0.3)",
                "&:disabled": {
                  bgcolor: "#ccc !important",
                  color: "#666"
                }
              }}
            >
              <PlayArrow sx={{ mr: 1 }} />
              {isCameraInitializing ? "Starting Camera..." : "Start Interview"}
            </Button>
          </Box>
        </Container>
      </Box>
    );
  }

  const renderFormattedCodingQuestion = (text, opts = {}) => {
    if (!text || text === "Waiting..." || text === "Waiting to start...") {
      return (
        <Typography sx={{ fontSize: opts.larger ? "17px" : "15px", fontWeight: 600, lineHeight: 1.7, color: opts.larger ? "#ffffff" : "#e2e8f0" }}>
          {text}
        </Typography>
      );
    }
    // Normalise both literal backslash-n and real newlines, then split
    const lines = text.replace(/\\n/g, '\n').split('\n');
    const SECTION_HEADERS = /^(problem statement|task|description|examples?|example \d+|sample inputs?|sample outputs?|input format|output format|constraints?|notes?|requirements?|functional requirements?|expected deliverables?|evaluation criteria|deliverables?|explanation|hints?|frontend challenge|sql challenge|coding challenge|database schema|schema|sample data|expected output)[\s:]*$/i;
    const INLINE_LABEL    = /^(input|output|expected output|stdin|stdout|sample input|sample output|constraints?|explanation|note)\s*:/i;
    const fontSize        = opts.larger ? "15px" : "12px";
    const headerSize      = opts.larger ? "11px" : "9px";
    const monoSize        = opts.larger ? "13px" : "11px";
    const baseColor       = opts.larger ? "#ffffff" : "#e2e8f0";
    const elements        = [];

    lines.forEach((raw, i) => {
      const line = raw.trimEnd();
      if (!line.trim()) {
        elements.push(<Box key={`sp-${i}`} sx={{ height: 6 }} />);
        return;
      }
      if (SECTION_HEADERS.test(line.trim())) {
        elements.push(
          <Typography key={`h-${i}`} sx={{ fontSize: headerSize, fontWeight: 700, color: '#58a6ff', textTransform: 'uppercase', letterSpacing: '0.8px', mt: elements.length > 0 ? 1.5 : 0, mb: 0.5 }}>
            {line.trim()}
          </Typography>
        );
        return;
      }
      if (INLINE_LABEL.test(line.trim())) {
        const colonIdx = line.indexOf(':');
        const label = line.substring(0, colonIdx).trim();
        const value = line.substring(colonIdx + 1).trim();
        elements.push(
          <Box key={`il-${i}`} sx={{ display: 'flex', gap: 0.75, mb: 0.35, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <Typography component="span" sx={{ fontSize: monoSize, fontWeight: 700, color: '#f0883e', flexShrink: 0 }}>{label}:</Typography>
            <Typography component="span" sx={{ fontSize: monoSize, color: '#c9d1d9', fontFamily: 'monospace' }}>{value}</Typography>
          </Box>
        );
        return;
      }
      elements.push(
        <Typography key={`t-${i}`} sx={{ fontSize, fontWeight: i === 0 ? 600 : 400, lineHeight: 1.65, color: baseColor, mb: 0.2 }}>
          {line}
        </Typography>
      );
    });

    return <Box>{elements}</Box>;
  };

  const isAITechnicalType = context?.interviewType === 'AI_TECHNICAL';
  const isCodingActive = (questions[currentQuestionIndex]?.type === "coding" || questions[currentQuestionIndex]?.type === "query") && stage === "questioning" && currentQuestionIndex >= 0 && !isAITechnicalType;
  const isArchitectureActive = questions[currentQuestionIndex]?.type === "architecture" && stage === "questioning" && currentQuestionIndex >= 0 && !isAITechnicalType;

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{

        height: "100vh",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column"
      }}
    >
      <Paper
        sx={{
          p: 0,
          borderRadius: 0,
          boxShadow: "none",
          bgcolor: "#fdfdfd",
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden"
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 3,
            py: 1.5,
            borderRadius: "0 !important",
            background: "#0f172a"
          }}
        >
          {/* LEFT SIDE */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "0 !important",
                bgcolor: "#14b8a6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              🤖
            </Box>

            <Typography sx={{ fontWeight: 600, color: "#FFFFFF", fontSize: "18px" }}>
              <Chip label={getInterviewTitle()} size="small" sx={{ color: "#FFFFFF", borderRadius: "0 !important" }} />
            </Typography>
          </Box>

          {/* RIGHT SIDE TIMER */}
          {interviewStarted && stage !== "finished" && (
            <Box
              sx={{
                bgcolor: "#1e293b",
                px: 2,
                py: 0.5,
                borderRadius: "0 !important",
                fontSize: "14px",
                fontWeight: 600,
                color: "#38bdf8"
              }}
            >
              ⏱ {formatTime(timeLeft)}
            </Box>
          )}
        </Box>


        {error && !["terminated", "completed", "expired"].includes(error) && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* ── Architecture drawing layout ─────────────────────────────── */}
        {isArchitectureActive && (
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <ArchitectureCanvas
              question={questions[currentQuestionIndex]}
              streamRef={streamRef}
              isCameraOn={isCameraOn}
              isMicOn={isMicOn}
              onSaveData={(data) => setArchCanvasData(data)}
              onNextQuestion={handleNextQuestion}
              showNextButton={showNextButton}
              loadingNext={loadingNext}
            />
          </Box>
        )}

        <Grid
          container
          spacing={0}
          sx={{
            flex: 1,
            minHeight: 0,
            display: isArchitectureActive ? 'none' : undefined
          }}
        >
          <Grid
            item
            xs={12}
            md={isCodingActive ? 4 : 7}
            sx={isCodingActive ? {
              display: "flex",
              flexDirection: "column",
              height: "100%",
              minHeight: 0,
              overflow: "hidden",
              background: "#0d1117",
              borderRight: "1px solid #1e293b",
            } : {
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "linear-gradient(180deg, #ffffff, #f3f4f6)",
              padding: "40px 0",
            }}
          >
            {/* ── Camera (single element, styles adapt) ── */}
            <Box
              sx={isCodingActive ? {
                flexShrink: 0,
                position: "relative",
                width: "100%",
                aspectRatio: "16/9",
                overflow: "hidden",
                bgcolor: "#000",
                borderBottom: "1px solid #1e293b",
              } : {
                position: "relative",
                width: "92%",
                maxWidth: "1000px",
                aspectRatio: "16 / 9",
                p: 0,
                bgcolor: "transparent",
                borderRadius: "0 !important",
                overflow: "hidden",
                boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                border: "none",
                outline: "none",
              }}
            >
              <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: "0 !important",
                  filter: "contrast(1.05) brightness(1.05)",
                  display: "block",
                }}
              />
              {!isCameraOn && (
                <Box sx={{
                  position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff'
                }}>
                  <VideocamOff sx={{ fontSize: isCodingActive ? 36 : 64, opacity: 0.5 }} />
                </Box>
              )}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: isCodingActive ? 6 : 16,
                  left: isCodingActive ? 6 : 16,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: 'rgba(0,0,0,0.6)', color: '#fff', px: 1.2, py: 0.4, borderRadius: '0 !important', fontSize: '11px' }}>
                  <Box sx={{ width: 6, height: 6, bgcolor: '#ff4d4f', borderRadius: '50%', animation: 'pulse 1.2s infinite' }} />
                  REC
                </Box>
                <IconButton size="small" sx={{ bgcolor: isMicOn ? 'rgba(0,0,0,0.5)' : 'error.main', color: '#fff', ...(isCodingActive && { width: 26, height: 26 }) }}>
                  {isMicOn ? <Mic sx={isCodingActive ? { fontSize: 13 } : {}} /> : <MicOff sx={isCodingActive ? { fontSize: 13 } : {}} />}
                </IconButton>
                <IconButton size="small" sx={{ bgcolor: isCameraOn ? 'rgba(0,0,0,0.5)' : 'error.main', color: '#fff', ...(isCodingActive && { width: 26, height: 26 }) }}>
                  {isCameraOn ? <Videocam sx={isCodingActive ? { fontSize: 13 } : {}} /> : <VideocamOff sx={isCodingActive ? { fontSize: 13 } : {}} />}
                </IconButton>
              </Box>
              {isListening && (
                <Box sx={{
                  position: 'absolute',
                  top: isCodingActive ? 6 : 16,
                  right: isCodingActive ? 6 : 16,
                  bgcolor: 'rgba(239,68,68,0.9)',
                  backdropFilter: "blur(4px)",
                  color: '#fff',
                  px: isCodingActive ? 1 : 2,
                  py: 0.5,
                  borderRadius: '0 !important',
                  display: 'flex',
                  alignItems: 'center',
                  gap: isCodingActive ? 0.5 : 1,
                  fontSize: isCodingActive ? '10px' : '12px',
                  fontWeight: 'bold',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                  animation: 'pulse 1.2s infinite'
                }}>
                  <Box sx={{ width: isCodingActive ? 5 : 8, height: isCodingActive ? 5 : 8, bgcolor: '#fff', borderRadius: '50%' }} />
                  LISTENING
                </Box>
              )}
            </Box>

            {/* ── Question card (coding / SQL mode only) ── */}
            {isCodingActive && (
              <Box sx={{ flex: 1, overflowY: "auto", width: "100%", p: 2, scrollbarWidth: "thin", scrollbarColor: "#334155 #0d1117" }}>
                {/* Header */}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5, pb: 1.25, borderBottom: "1px solid #1e293b", flexShrink: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Avatar variant="square" sx={{ bgcolor: "#4F46E5", width: 28, height: 28 }}>
                      <SmartToy sx={{ fontSize: 16 }} />
                    </Avatar>
                    <Typography sx={{ fontWeight: 700, fontSize: "13px", color: "#FFFFFF" }}>{getInterviewerName()}</Typography>
                    {isAISpeaking && <CircularProgress size={10} />}
                  </Box>
                  {interviewStarted && questions.length > 0 && (
                    <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#64748b" }}>Q {currentQuestionIndex + 1}</Typography>
                  )}
                </Box>
                {/* Question content */}
                {lockedCompiler === "sql" && (questions[currentQuestionIndex]?.type === "coding" || questions[currentQuestionIndex]?.type === "query") && aiResponse ? (() => {
                  const parsed = parseSQLQuestion(aiResponse);
                  const schemaLines = parsed?.schema ? parsed.schema.split('\n').filter(l => l.trim()) : [];
                  const needsCollapse = schemaLines.length > 6;
                  const levelLower = context?.level?.toLowerCase();
                  const diffLabel = levelLower === 'experienced' ? 'Hard' : levelLower === 'mid' ? 'Medium' : 'Easy';
                  const diffColor = levelLower === 'experienced' ? '#f85149' : levelLower === 'mid' ? '#e3b341' : '#3fb950';
                  if (!parsed || (!parsed.schema && parsed.task.length === 0)) {
                    return <Typography sx={{ fontSize: '14px', fontWeight: 600, lineHeight: 1.65, color: '#e2e8f0', mt: 0.5 }}>{aiResponse}</Typography>;
                  }
                  return (
                    <Box sx={{ mt: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
                        <Typography sx={{ fontSize: '13px', lineHeight: 1 }}>🗃️</Typography>
                        <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#e2e8f0' }}>SQL Challenge</Typography>
                        <Chip size="small" label={diffLabel} sx={{ bgcolor: diffColor + '22', color: diffColor, border: `1px solid ${diffColor}55`, fontSize: '10px', height: 18, fontWeight: 700, borderRadius: "0 !important", '& .MuiChip-label': { px: 0.75 } }} />
                      </Box>
                      {parsed.schema && (
                        <Box sx={{ mb: 1.5, borderRadius: 0, bgcolor: '#0d1117', border: '1px solid #30363d', overflow: 'hidden' }}>
                          <Box sx={{ px: 1.25, py: 0.6, bgcolor: '#161b22', borderBottom: '1px solid #30363d', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography sx={{ fontSize: '9px', fontWeight: 700, color: '#58a6ff', fontFamily: 'monospace', letterSpacing: '1px' }}>📊 DATABASE SCHEMA</Typography>
                            {needsCollapse && (
                              <Button size="small" onClick={() => setSchemaExpanded(v => !v)} sx={{ fontSize: '9px', color: '#8b949e', minWidth: 'auto', px: 0.5, py: 0, lineHeight: 1.8, textTransform: 'none' }}>
                                {schemaExpanded ? '▲ Less' : '▼ More'}
                              </Button>
                            )}
                          </Box>
                          <Box sx={{ p: 1.25, maxHeight: needsCollapse && !schemaExpanded ? '72px' : '400px', overflow: 'hidden', transition: 'max-height 0.25s ease' }}>
                            {schemaLines.map((line, i) => renderSchemaLine(line, i))}
                          </Box>
                        </Box>
                      )}
                      {parsed.sampleData && (
                        <Box sx={{ mb: 1.25 }}>
                          <Typography sx={{ fontSize: '9px', fontWeight: 700, color: '#79c0ff', mb: 0.6, textTransform: 'uppercase', letterSpacing: '0.8px' }}>Sample Data</Typography>
                          <Box sx={{ bgcolor: '#0d1117', border: '1px solid #30363d', borderRadius: 0, p: 1, fontFamily: 'monospace', fontSize: '10px', color: '#c9d1d9', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                            {parsed.sampleData}
                          </Box>
                        </Box>
                      )}
                      {parsed.task.length > 0 && (
                        <Box sx={{ mb: 1.25 }}>
                          <Typography sx={{ fontSize: '9px', fontWeight: 700, color: '#f0883e', mb: 0.6, textTransform: 'uppercase', letterSpacing: '0.8px' }}>Task</Typography>
                          {parsed.task.map((item, i) => (
                            <Box key={i} sx={{ display: 'flex', gap: 0.6, mb: 0.4, alignItems: 'flex-start' }}>
                              <Box component="span" sx={{ color: '#58a6ff', fontSize: '11px', mt: '3px', flexShrink: 0, lineHeight: 1 }}>▸</Box>
                              <Typography sx={{ fontSize: '12px', color: '#e2e8f0', lineHeight: 1.6 }}>{item}</Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                      {parsed.constraints.length > 0 && (
                        <Box sx={{ mb: 1.25 }}>
                          <Typography sx={{ fontSize: '9px', fontWeight: 700, color: '#f85149', mb: 0.6, textTransform: 'uppercase', letterSpacing: '0.8px' }}>Constraints</Typography>
                          {parsed.constraints.map((c, i) => (
                            <Box key={i} sx={{ display: 'flex', gap: 0.6, mb: 0.3, alignItems: 'flex-start' }}>
                              <Box component="span" sx={{ color: '#f85149', fontSize: '10px', mt: '3px', flexShrink: 0, lineHeight: 1 }}>!</Box>
                              <Typography sx={{ fontSize: '11px', color: '#c9d1d9', lineHeight: 1.5 }}>{c.replace(/^[-•*·]\s*/, '')}</Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                      {parsed.expectedOutput && (
                        <Box>
                          <Typography sx={{ fontSize: '9px', fontWeight: 700, color: '#3fb950', mb: 0.6, textTransform: 'uppercase', letterSpacing: '0.8px' }}>Expected Output</Typography>
                          <Box sx={{ bgcolor: '#0d1117', border: '1px solid #30363d', borderRadius: 0, p: 1, fontFamily: 'monospace', fontSize: '10px', color: '#c9d1d9', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                            {parsed.expectedOutput}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  );
                })() : (
                  renderFormattedCodingQuestion(aiResponse || "Waiting...")
                )}

                {/* ── Public test cases panel ───────────────────────────────── */}
                {(() => {
                  const vTc = questions[currentQuestionIndex]?.visibleTestCases;
                  if (!vTc || vTc.length === 0) return null;
                  return (
                    <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid #1e293b' }}>
                      <Typography sx={{
                        fontSize: '9px', fontWeight: 700, color: '#58a6ff',
                        textTransform: 'uppercase', letterSpacing: '0.8px', mb: 1,
                      }}>
                        📋 Test Cases ({vTc.length} public)
                      </Typography>
                      {vTc.map((tc, idx) => (
                        <Box key={idx} sx={{ mb: 1.25, bgcolor: '#0d1117', border: '1px solid #21262d', overflow: 'hidden' }}>
                          <Box sx={{ px: 1.25, py: 0.4, bgcolor: '#161b22', borderBottom: '1px solid #21262d' }}>
                            <Typography sx={{ fontSize: '9px', fontWeight: 700, color: '#8b949e', fontFamily: 'monospace' }}>
                              Case {idx + 1}
                            </Typography>
                          </Box>
                          <Box sx={{ p: 1 }}>
                            {tc.input != null && tc.input !== '' && (
                              <Box sx={{ mb: 0.75 }}>
                                <Typography sx={{ fontSize: '9px', color: '#f0883e', fontWeight: 600, mb: 0.25 }}>Input</Typography>
                                <Box component="pre" sx={{ m: 0, fontFamily: 'monospace', fontSize: '11px', color: '#c9d1d9', whiteSpace: 'pre-wrap', lineHeight: 1.5, pl: 0.5 }}>
                                  {tc.input}
                                </Box>
                              </Box>
                            )}
                            {(tc.expectedOutput ?? tc.expected ?? tc.expected_output) != null && (
                              <Box>
                                <Typography sx={{ fontSize: '9px', color: '#3fb950', fontWeight: 600, mb: 0.25 }}>Expected Output</Typography>
                                <Box component="pre" sx={{ m: 0, fontFamily: 'monospace', fontSize: '11px', color: '#c9d1d9', whiteSpace: 'pre-wrap', lineHeight: 1.5, pl: 0.5 }}>
                                  {tc.expectedOutput ?? tc.expected ?? tc.expected_output}
                                </Box>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  );
                })()}
              </Box>
            )}
          </Grid>

          <Grid
            item
            xs={12}
            md={isCodingActive ? 8 : 5}
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              overflow: "hidden"
            }}
          >
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden"
              }}
            >
              <Paper
               className={isCodingActive ? undefined : "interview-scroll"}
                sx={{
                  p: isCodingActive ? 0 : 2,
                  flex: 1,
                  height: "100%",
                  overflowY: isCodingActive ? "hidden" : "auto",
                  overflowX: "hidden",
                  minHeight: 0,
                  border: isCodingActive ? "none" : "1px solid rgba(255,255,255,0.08)",
                  borderRadius: "0 !important",
                  background: "linear-gradient(180deg, #0f172a 0%, #020617 100%)",
                  color: "#ffffff",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start"
                }}
              >

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: isCodingActive ? 0 : 2,
                  ...(isCodingActive ? { flex: 1, minHeight: 0, overflow: "hidden" } : { flexGrow: 1, minHeight: "120%", overflow: "visible" }),
                }}
              >
                {!isCodingActive && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1
                  }}
                >
                  {/* LEFT SIDE */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Avatar variant="square" sx={{ bgcolor: "#4F46E5", width: 32, height: 32 }}>
                      <SmartToy fontSize="small" />
                    </Avatar>

                    <Typography sx={{ fontWeight: "bold", fontSize: "14px", color: "#FFFFFF" }}>
                      {getInterviewerName()}
                    </Typography>

                    {isAISpeaking && <CircularProgress size={12} sx={{ ml: 1 }} />}
                  </Box>

                  {/* RIGHT SIDE (QUESTION NUMBER) */}
                  {interviewStarted && questions.length > 0 && (
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: "bold",
                        color: "#ffffff",
                        opacity: 0.9
                      }}
                    >
                      Question {currentQuestionIndex + 1}
                    </Typography>
                  )}
                </Box>
                )}

                {!isCodingActive && (
                <Box sx={{ flexShrink: 0, overflowY: "visible", scrollbarWidth: "thin" }}>
                {lockedCompiler === "sql" && (questions[currentQuestionIndex]?.type === "coding" || questions[currentQuestionIndex]?.type === "query") && aiResponse ? (() => {
                  const parsed = parseSQLQuestion(aiResponse);
                  const schemaLines = parsed?.schema ? parsed.schema.split('\n').filter(l => l.trim()) : [];
                  const needsCollapse = schemaLines.length > 8;
                  const levelLower = context?.level?.toLowerCase();
                  const diffLabel = levelLower === 'experienced' ? 'Hard' : levelLower === 'mid' ? 'Medium' : 'Easy';
                  const diffColor = levelLower === 'experienced' ? '#f85149' : levelLower === 'mid' ? '#e3b341' : '#3fb950';
                  if (!parsed || (!parsed.schema && parsed.task.length === 0)) {
                    return <Typography sx={{ fontSize: '17px', fontWeight: 600, lineHeight: 1.6, color: '#ffffff', mt: 0.5, mb: 2 }}>{aiResponse}</Typography>;
                  }
                  return (
                    <Box sx={{ mt: 0.5, mb: 2 }}>
                      {/* Title + difficulty badge */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
                        <Typography sx={{ fontSize: '14px', lineHeight: 1 }}>🗃️</Typography>
                        <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#e2e8f0' }}>SQL Challenge</Typography>
                        <Chip size="small" label={diffLabel} sx={{ bgcolor: diffColor + '22', color: diffColor, border: `1px solid ${diffColor}55`, fontSize: '10px', height: 18, fontWeight: 700, borderRadius: "0 !important", '& .MuiChip-label': { px: 0.75 } }} />
                      </Box>

                      {/* Database Schema Card */}
                      {parsed.schema && (
                        <Box sx={{ mb: 2, borderRadius: '0 !important', bgcolor: '#0d1117', border: '1px solid #30363d', overflow: 'hidden' }}>
                          <Box sx={{ px: 1.5, py: 0.75, bgcolor: '#161b22', borderBottom: '1px solid #30363d', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#58a6ff', fontFamily: 'monospace', letterSpacing: '1px' }}>📊 DATABASE SCHEMA</Typography>
                            {needsCollapse && (
                              <Button size="small" onClick={() => setSchemaExpanded(v => !v)}
                                sx={{ fontSize: '10px', color: '#8b949e', minWidth: 'auto', px: 0.75, py: 0, lineHeight: 1.8, textTransform: 'none', '&:hover': { color: '#c9d1d9' } }}>
                                {schemaExpanded ? '▲ Show less' : '▼ Show more'}
                              </Button>
                            )}
                          </Box>
                          <Box sx={{ p: 1.5, maxHeight: needsCollapse && !schemaExpanded ? '88px' : '500px', overflow: 'hidden', transition: 'max-height 0.25s ease' }}>
                            {schemaLines.map((line, i) => renderSchemaLine(line, i))}
                          </Box>
                        </Box>
                      )}

                      {/* Sample Data */}
                      {parsed.sampleData && (
                        <Box sx={{ mb: 2 }}>
                          <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#79c0ff', mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.8px' }}>Sample Data</Typography>
                          <Box sx={{ bgcolor: '#0d1117', border: '1px solid #30363d', borderRadius: '0 !important', p: 1, fontFamily: 'monospace', fontSize: '11px', color: '#c9d1d9', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                            {parsed.sampleData}
                          </Box>
                        </Box>
                      )}

                      {/* Task / Problem Statement */}
                      {parsed.task.length > 0 && (
                        <Box sx={{ mb: 1.5 }}>
                          <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#f0883e', mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.8px' }}>Task</Typography>
                          {parsed.task.map((item, i) => (
                            <Box key={i} sx={{ display: 'flex', gap: 0.75, mb: 0.5, alignItems: 'flex-start' }}>
                              <Box component="span" sx={{ color: '#58a6ff', fontSize: '12px', mt: '3px', flexShrink: 0, lineHeight: 1 }}>▸</Box>
                              <Typography sx={{ fontSize: '13px', color: '#e2e8f0', lineHeight: 1.65 }}>{item}</Typography>
                            </Box>
                          ))}
                        </Box>
                      )}

                      {/* Constraints */}
                      {parsed.constraints.length > 0 && (
                        <Box sx={{ mb: 1.5 }}>
                          <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#f85149', mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.8px' }}>Constraints</Typography>
                          {parsed.constraints.map((c, i) => (
                            <Box key={i} sx={{ display: 'flex', gap: 0.75, mb: 0.4, alignItems: 'flex-start' }}>
                              <Box component="span" sx={{ color: '#f85149', fontSize: '11px', mt: '3px', flexShrink: 0, lineHeight: 1 }}>!</Box>
                              <Typography sx={{ fontSize: '12px', color: '#c9d1d9', lineHeight: 1.55 }}>{c.replace(/^[-•*·]\s*/, '')}</Typography>
                            </Box>
                          ))}
                        </Box>
                      )}

                      {/* Expected Output */}
                      {parsed.expectedOutput && (
                        <Box sx={{ mb: 0.5 }}>
                          <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#3fb950', mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.8px' }}>Expected Output</Typography>
                          <Box sx={{ bgcolor: '#0d1117', border: '1px solid #30363d', borderRadius: '0 !important', p: 1, fontFamily: 'monospace', fontSize: '11px', color: '#c9d1d9', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                            {parsed.expectedOutput}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  );
                })() : (
                  <Box sx={{ mt: 0.5, mb: 2 }}>
                    {renderFormattedCodingQuestion(aiResponse || "Waiting to start...", { larger: true })}
                  </Box>
                )}
                </Box>
                )}

                {stage === "finished" && !isTerminated && (
                  <>
                    <Typography
                       sx={{
                         mt: 2,
                         mb: 1,   // 🔥 reduce gap
                         fontSize: "14px",
                         color: "#22c55e",
                         fontWeight: 500
                       }}
                     >
                       Interview completed successfully. Click "Complete & Submit" to finish.
                     </Typography>

                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      onClick={() => handleSubmitInterview(false)}
                      sx={{
                        mt: 1,   // 🔥 IMPORTANT (was too much gap visually)
                        py: 1.5,
                        borderRadius: "0 !important",
                        fontWeight: 600,
                        background: "linear-gradient(135deg, #14b8a6, #0ea5e9)",
                        boxShadow: "0 6px 20px rgba(0,0,0,0.25)",
                        "&:hover": {
                          background: "linear-gradient(135deg, #0d9488, #0284c7)",
                        }
                      }}
                      disabled={loading}
                    >
                      {loading ? <CircularProgress size={24} /> : 'Complete & Submit'}
                    </Button>
                  </>
                 )}


                  {stage === "questioning" && currentQuestionIndex >= 0 && (
                    <Box sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: isCodingActive ? 0 : 2,
                      ...(isCodingActive ? { flex: 1, minHeight: 0, overflow: "hidden" } : {}),
                    }}>
                      {!isCodingActive && (
                      <Typography
                        sx={{
                          fontSize: "16px",
                          fontWeight: 600,
                          color: "rgba(255,255,255,0.85)",
                          mb: 1,
                          flexShrink: 0,
                        }}
                      >
                        Your Response:
                      </Typography>
                      )}


                  {/* ✅ SHOW CODING EDITOR FOR CODING AND QUERY */}
                  {(questions[currentQuestionIndex]?.type === "coding" || questions[currentQuestionIndex]?.type === "query") && (
                    <>


                      <Box
                        sx={{
                          ...(isCodingActive
                            ? { flex: 1, minHeight: 0, mt: 0, borderRadius: 0, border: "none" }
                            : { mt: 1, borderRadius: 0, border: "1px solid #ccc" }),
                          overflow: "hidden",
                          bgcolor: "#1e1e1e",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            flex: 1,
                            minHeight: 0,
                            bgcolor: "#1e1e1e",
                            overflow: "hidden",
                            border: "1px solid #333",
                            borderRadius: 0,
                          }}
                        >
                          {/* Top Toolbar */}
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              px: 2,
                              py: 1,
                              bgcolor: "#2d2d2d",
                              color: "#fff",
                              borderBottom: "1px solid #444",
                              borderRadius: 0
                            }}
                          >
                            <Stack direction="row" spacing={2} alignItems="center">
                              {lockedCompiler ? (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                  <Lock sx={{ fontSize: 14, color: "#7eb8f7" }} />
                                  <Chip
                                    size="small"
                                    label={SUPPORTED_LANGUAGES.find(l => l.value === lockedCompiler)?.label || lockedCompiler.toUpperCase()}
                                    sx={{ bgcolor: "#1e3a5f", color: "#7eb8f7", border: "1px solid #3a6ea8", height: 28, fontWeight: 600, fontSize: "12px" }}
                                  />
                                </Box>
                              ) : (
                                <FormControl size="small" sx={{ minWidth: 150 }}>
                                  <Select
                                    displayEmpty
                                    value={language}
                                    onChange={(e) => handleLanguageChange(e.target.value)}
                                    sx={{
                                      height: "32px",
                                      fontSize: "13px",
                                      color: "#fff !important",
                                      "& .MuiSelect-select": { color: "#fff !important" },
                                      ".MuiOutlinedInput-notchedOutline": { borderColor: "#555" },
                                      "& .MuiSvgIcon-root": { color: "#fff !important" },
                                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#888" },
                                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#00ff9c" }
                                    }}
                                    MenuProps={{ PaperProps: { sx: { bgcolor: "#2d2d2d", color: "#fff !important" } } }}
                                  >
                                    <MenuItem value="" sx={{ color: "#fff !important" }}><em>-- select --</em></MenuItem>
                                    {SUPPORTED_LANGUAGES.map((lang) => (
                                      <MenuItem key={lang.value} value={lang.value} sx={{ color: "#fff !important" }}>{lang.label}</MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              )}

                              {lockedCompiler !== "frontend" && (
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<PlayArrow />}
                                onClick={runCode}
                                disabled={isExecuting || !language}
                                sx={{
                                  height: "32px",
                                  bgcolor: "#28a745",
                                  "&:hover": { bgcolor: "#218838" },
                                  textTransform: "none",
                                  borderRadius: "0 !important"
                                }}
                              >
                                Run
                              </Button>
                              )}

                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<Stop />}
                                onClick={stopCode}
                                disabled={!isExecuting}
                                sx={{
                                  height: "32px",
                                  bgcolor: "#dc3545",
                                  "&:hover": { bgcolor: "#c82333" },
                                  textTransform: "none",
                                  borderRadius: "0 !important"
                                }}
                              >
                                Stop
                              </Button>

                              <Button
                                size="small"
                                variant="outlined"
                                onClick={clearTerminal}
                                sx={{
                                  height: "32px",
                                  color: "#ccc",
                                  borderColor: "#555",
                                  "&:hover": { borderColor: "#888", color: "#fff" },
                                  textTransform: "none",
                                  borderRadius: "0 !important"
                                }}
                              >
                                Clear
                              </Button>

                              <Button
                                size="small"
                                variant="outlined"
                                onClick={saveCode}
                                sx={{
                                  height: "32px",
                                  color: "#ccc",
                                  borderColor: "#555",
                                  "&:hover": { borderColor: "#888", color: "#fff" },
                                  textTransform: "none",
                                  borderRadius: "0 !important"
                                }}
                              >
                                Save Answer
                              </Button>
                            </Stack>

                            {/* ── Auto-save status indicator ── */}
                            {interviewStarted && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto', pl: 1 }}>
                                {autoSaveStatus === 'saving' && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#94a3b8', fontSize: '10px' }}>
                                    <CircularProgress size={8} sx={{ color: '#94a3b8' }} />
                                    Saving…
                                  </Box>
                                )}
                                {autoSaveStatus === 'saved' && lastSavedAt && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, color: '#3fb950', fontSize: '10px' }}>
                                    <Check sx={{ fontSize: 11 }} />
                                    Saved {lastSavedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                  </Box>
                                )}
                                {autoSaveStatus === 'offline' && (
                                  <Box sx={{ color: '#e3b341', fontSize: '10px' }}>
                                    ⚠ Offline — changes pending sync
                                  </Box>
                                )}
                                {autoSaveStatus === 'error' && (
                                  <Box sx={{ color: '#f85149', fontSize: '10px' }}>
                                    ✕ Save failed — retrying…
                                  </Box>
                                )}
                              </Box>
                            )}
                          </Box>

                          {/* Editor Area */}
                          {lockedCompiler === "frontend" ? (
                            <Box sx={{ flexGrow: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                              {/* HTML / CSS tab bar */}
                              <Box sx={{ display: "flex", bgcolor: "#252526", borderBottom: "1px solid #3a3a3a" }}>
                                {["html", "css"].map(tab => (
                                  <Button
                                    key={tab}
                                    size="small"
                                    onClick={() => setFrontendTab(tab)}
                                    sx={{
                                      px: 2, py: 0.5, borderRadius: "0 !important", textTransform: "none", minWidth: 60,
                                      bgcolor: frontendTab === tab ? "#1e1e1e" : "transparent",
                                      color: frontendTab === tab ? "#fff" : "#888",
                                      borderBottom: frontendTab === tab ? "2px solid #007acc" : "2px solid transparent",
                                      fontSize: "12px",
                                      "&:hover": { bgcolor: "#2d2d2d", color: "#fff" }
                                    }}
                                  >{tab.toUpperCase()}</Button>
                                ))}
                              </Box>
                              <Box sx={{ flexGrow: 1, minHeight: 0, position: "relative" }}>
                                {frontendTab === "html" ? (
                                  <Editor
                                    height="100%"
                                    language="html"
                                    value={htmlCode}
                                    theme="vs-dark"
                                    onChange={(val) => setHtmlCode(val || "")}
                                    options={{ minimap: { enabled: false }, fontSize: 14, scrollBeyondLastLine: false, automaticLayout: true }}
                                  />
                                ) : (
                                  <Editor
                                    height="100%"
                                    language="css"
                                    value={cssCode}
                                    theme="vs-dark"
                                    onChange={(val) => setCssCode(val || "")}
                                    options={{ minimap: { enabled: false }, fontSize: 14, scrollBeyondLastLine: false, automaticLayout: true }}
                                  />
                                )}
                              </Box>
                            </Box>
                          ) : (
                          <Box sx={{ flexGrow: 1, minHeight: 0, position: "relative" }}>
                            <Editor
                              height="100%"
                              language={SUPPORTED_LANGUAGES.find(l => l.value === language)?.monaco || "plaintext"}
                              value={code}
                              theme="vs-dark"
                              onChange={(val) => setCode(val)}
                              options={{
                                minimap: { enabled: false },
                                fontSize: 14,
                                scrollBeyondLastLine: false,
                                automaticLayout: true,
                                scrollbar: {
                                  vertical: 'visible',
                                  horizontal: 'visible',
                                  verticalScrollbarSize: 10,
                                  horizontalScrollbarSize: 10
                                }
                              }}
                            />
                          </Box>
                          )}

                          {/* Resizable Divider */}
                          <Box
                            sx={{
                              height: "4px",
                              bgcolor: "#444",
                              cursor: "ns-resize",
                              "&:hover": { bgcolor: "#00ff9c" }
                            }}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              const startY = e.clientY;
                              const startH = terminalHeight;
                              const maxTerminalH = Math.max(80, Math.floor(compilerPanelHeight * 0.6));
                              const onMouseMove = (moveE) => {
                                const newH = startH - (moveE.clientY - startY);
                                if (newH >= 50 && newH <= maxTerminalH) setTerminalHeight(newH);
                              };
                              const onMouseUp = () => {
                                window.removeEventListener("mousemove", onMouseMove);
                                window.removeEventListener("mouseup", onMouseUp);
                                document.body.style.cursor = "";
                                document.body.style.userSelect = "";
                              };
                              document.body.style.cursor = "ns-resize";
                              document.body.style.userSelect = "none";
                              window.addEventListener("mousemove", onMouseMove);
                              window.addEventListener("mouseup", onMouseUp);
                            }}
                          />

                          {/* Terminal / Preview Area */}
                          {lockedCompiler === "frontend" ? (
                            <Box
                              sx={{
                                height: `${terminalHeight}px`,
                                display: "flex",
                                flexDirection: "column",
                                borderTop: "1px solid #333",
                                bgcolor: "#fff"
                              }}
                            >
                              <Box sx={{ px: 1, py: 0.5, bgcolor: "#1a1a1a", borderBottom: "1px solid #333", display: "flex", justifyContent: "space-between" }}>
                                <Typography variant="caption" sx={{ color: "#aaa" }}>Live Preview</Typography>
                                <Typography variant="caption" sx={{ color: "#aaa", cursor: "pointer" }} onClick={() => setTerminalHeight(200)}>Reset</Typography>
                              </Box>
                              <iframe
                                title="frontend-preview"
                                ref={previewIframeRef}
                                srcDoc={`<!DOCTYPE html><html><head><style>${cssCode}</style></head><body>${CONSOLE_CAPTURE_SCRIPT}${htmlCode}</body></html>`}
                                style={{ flexGrow: 1, width: "100%", border: "none", background: "#fff" }}
                                sandbox="allow-scripts allow-same-origin"
                              />
                            </Box>
                          ) : (
                            <Box
                              sx={{
                                height: `${terminalHeight}px`,
                                bgcolor: "#000",
                                color: "#00ff00",
                                fontFamily: "monospace",
                                fontSize: "13px",
                                display: "flex",
                                flexDirection: "column",
                                borderTop: "1px solid #333",
                                borderRadius: 0,
                                overflow: "hidden"
                              }}
                            >
                              <Box sx={{ display: "flex", justifyContent: "space-between", px: 1, py: 0.5, bgcolor: "#1a1a1a", borderBottom: "1px solid #333" }}>
                                <Typography variant="caption" sx={{ color: "#aaa" }}>Terminal</Typography>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                  <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <Typography variant="caption" sx={{ color: "#aaa", mr: 1 }}>stdin</Typography>
                                    <input
                                      type="checkbox"
                                      checked={showStdin}
                                      onChange={(e) => setShowStdin(e.target.checked)}
                                      style={{ cursor: "pointer" }}
                                    />
                                  </Box>
                                  <Typography variant="caption" sx={{ color: "#aaa", cursor: "pointer" }} onClick={() => setTerminalHeight(200)}>Reset</Typography>
                                </Box>
                              </Box>

                              <Box sx={{ flexGrow: 1, display: "flex", overflow: "hidden" }}>
                                {showStdin && (
                                  <Box sx={{ width: "30%", borderRight: "1px solid #333", display: "flex", flexDirection: "column" }}>
                                    <Box sx={{ p: 0.5, bgcolor: "#111", fontSize: "11px", color: "#888" }}>INPUT (stdin)</Box>
                                    <textarea
                                      value={stdin}
                                      onChange={(e) => setStdin(e.target.value)}
                                      style={{
                                        flexGrow: 1,
                                        background: "#000",
                                        color: "#fff",
                                        border: "none",
                                        outline: "none",
                                        padding: "8px",
                                        resize: "none",
                                        fontFamily: "monospace"
                                      }}
                                    />
                                  </Box>
                                )}
                                <Box
                                  sx={{
                                    flexGrow: 1,
                                    p: 1,
                                    overflowY: "auto",
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-all"
                                  }}
                                >
                                  {isExecuting ? (
                                    <>
                                      {terminalOutput}
                                      <CircularProgress size={14} sx={{ ml: 1, color: "#00ff00" }} />
                                    </>
                                  ) : language === 'sql' && (sqlQueryResult || (terminalOutput && terminalOutput.includes('Query executed'))) ? (
                                    // ── SQL results grid ──────────────────────────────────────────────
                                    <Box sx={{ whiteSpace: 'normal' }}>
                                      <Box sx={{ color: '#00ff9c', fontSize: '12px', mb: 1 }}>
                                        {terminalOutput}
                                      </Box>
                                      {sqlQueryResult && sqlQueryResult.headers ? (
                                        <Box sx={{ overflowX: 'auto' }}>
                                          <table style={{ borderCollapse: 'collapse', fontSize: '12px', width: '100%' }}>
                                            <thead>
                                              <tr>
                                                {sqlQueryResult.headers.map((h, hi) => (
                                                  <th key={hi} style={{
                                                    padding: '6px 14px',
                                                    textAlign: 'left',
                                                    backgroundColor: '#0f2744',
                                                    borderBottom: '2px solid #1e5fa8',
                                                    color: '#7eb8f7',
                                                    fontWeight: 700,
                                                    whiteSpace: 'nowrap',
                                                    fontFamily: 'monospace',
                                                  }}>
                                                    {h}
                                                  </th>
                                                ))}
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {sqlQueryResult.rows && sqlQueryResult.rows.length > 0 ? (
                                                sqlQueryResult.rows.map((row, ri) => (
                                                  <tr key={ri} style={{ backgroundColor: ri % 2 === 0 ? '#0d1117' : '#111827' }}>
                                                    {row.map((cell, ci) => (
                                                      <td key={ci} style={{
                                                        padding: '5px 14px',
                                                        borderBottom: '1px solid #1e2d3d',
                                                        color: '#e2e8f0',
                                                        whiteSpace: 'nowrap',
                                                        fontFamily: 'monospace',
                                                      }}>
                                                        {cell}
                                                      </td>
                                                    ))}
                                                  </tr>
                                                ))
                                              ) : (
                                                <tr>
                                                  <td colSpan={sqlQueryResult.headers.length} style={{ padding: '10px', textAlign: 'center', color: '#718096' }}>
                                                    No rows returned
                                                  </td>
                                                </tr>
                                              )}
                                            </tbody>
                                          </table>
                                          {sqlQueryResult.rows && (
                                            <Box sx={{ mt: 0.75, fontSize: '11px', color: '#718096' }}>
                                              {sqlQueryResult.rows.length} row(s) returned
                                            </Box>
                                          )}
                                        </Box>
                                      ) : (
                                        !terminalOutput.includes('Error:') && (
                                          <Box sx={{ color: '#718096', fontSize: '12px', mt: 0.5 }}>
                                            No results to display.
                                          </Box>
                                        )
                                      )}

                                      {/* SQL hidden test case score panel */}
                                      {sqlHiddenTestResults.length > 0 && (
                                        <Box sx={{ mt: 1.5, p: 1, bgcolor: '#0a1628', borderRadius: 1, border: '1px solid #1e3a5f' }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                            <Box sx={{ fontSize: '12px', fontWeight: 700,
                                              color: sqlTierScore === 100 ? '#10b981'
                                                   : sqlTierScore >= 80  ? '#3b82f6'
                                                   : sqlTierScore >= 60  ? '#f59e0b'
                                                   : sqlTierScore >= 30  ? '#f97316'
                                                   : '#ef4444' }}>
                                              Score: {sqlTierScore}/100
                                            </Box>
                                            <Box sx={{ fontSize: '11px', color: '#64748b' }}>|</Box>
                                            <Box sx={{ fontSize: '11px', color: '#94a3b8' }}>
                                              Hidden Tests: {sqlHiddenTestResults.filter(r => r.passed).length}/{sqlHiddenTestResults.length} passed
                                            </Box>
                                            {sqlHiddenTestResults.map((r, i) => (
                                              <Box key={i} sx={{ fontSize: '10px', px: 0.75, py: 0.25, borderRadius: 0.5,
                                                bgcolor: r.passed ? '#064e3b' : '#7f1d1d',
                                                color: r.passed ? '#34d399' : '#fca5a5',
                                                border: `1px solid ${r.passed ? '#065f46' : '#991b1b'}` }}>
                                                {r.datasetType}: {r.passed ? '✓' : '✗'}
                                              </Box>
                                            ))}
                                          </Box>
                                          {sqlFeedback.length > 0 && (
                                            <Box sx={{ mt: 0.75, fontSize: '11px', color: '#fbbf24' }}>
                                              {sqlFeedback.map((msg, i) => (
                                                <Box key={i}>• {msg}</Box>
                                              ))}
                                            </Box>
                                          )}
                                        </Box>
                                      )}
                                    </Box>
                                  ) : (
                                    terminalOutput
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          )}

                          {/* Test Cases Area */}
                          {!isExecuting && testCaseResults && testCaseResults.length > 0 && (
                            <Box
                              sx={{
                                maxHeight: "300px",
                                overflowY: "auto",
                                bgcolor: "#0f172a",
                                borderTop: "1px solid #334155",
                                p: 2,
                                color: "#f8fafc"
                              }}
                            >
                              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <span>Test Cases</span>
                                <Box sx={{ fontSize: "12px", display: "flex", gap: 2 }}>
                                  {testCaseResults && testCaseResults.length > 0 && (
                                    <Box sx={{ color: "#10b981" }}>Passed: {testCaseResults.filter(r => r.passed).length}/{testCaseResults.length}</Box>
                                  )}
                                  {executionStats.time && <Box sx={{ color: "#94a3b8" }}>Time: {executionStats.time}ms</Box>}
                                  {executionStats.memory && <Box sx={{ color: "#94a3b8" }}>Memory: {Math.round(executionStats.memory/1024)}KB</Box>}
                                </Box>
                              </Typography>

                              <Grid container spacing={2}>
                                {testCaseResults.map((tc, idx) => (
                                  <Grid item xs={12} key={idx}>
                                    <Box sx={{
                                      p: 1.5,
                                      bgcolor: "#1e293b",
                                      border: "1px solid",
                                      borderColor: tc.passed ? "#064e3b" : "#7f1d1d",
                                      borderRadius: 1
                                    }}>
                                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                                        <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
                                          TC{idx + 1} {tc.passed ? "✅ Pass" : "❌ Fail"}
                                        </Typography>
                                      </Box>

                                      <Box sx={{ fontSize: "12px", fontFamily: "monospace" }}>
                                        <Box sx={{ mb: 1 }}>
                                          <Box sx={{ color: "#94a3b8" }}>Input:</Box>
                                          <Box sx={{ p: 1, bgcolor: "#0f172a", borderRadius: 0.5, mt: 0.5, whiteSpace: "pre-wrap" }}>
                                            {tc.input || "(no input)"}
                                          </Box>
                                        </Box>

                                        <Grid container spacing={1}>
                                          <Grid item xs={6}>
                                            <Box sx={{ color: "#94a3b8" }}>Expected:</Box>
                                            <Box sx={{ p: 1, bgcolor: "#0f172a", borderRadius: 0.5, mt: 0.5, whiteSpace: "pre-wrap", minHeight: "30px" }}>
                                              {tc.expectedOutput}
                                            </Box>
                                          </Grid>
                                          <Grid item xs={6}>
                                            <Box sx={{ color: "#94a3b8" }}>Actual:</Box>
                                            <Box sx={{ p: 1, bgcolor: "#0f172a", borderRadius: 0.5, mt: 0.5, whiteSpace: "pre-wrap", minHeight: "30px", color: tc.passed ? "#10b981" : "#ef4444" }}>
                                              {tc.actualOutput || "(no output)"}
                                            </Box>
                                          </Grid>
                                        </Grid>
                                      </Box>
                                    </Box>
                                  </Grid>
                                ))}
                              </Grid>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </>
                  )}
                  {questions[currentQuestionIndex]?.type !== "coding" && questions[currentQuestionIndex]?.type !== "query" && (
                    <TextField
                      fullWidth
                      multiline
                      minRows={5}
                      value={transcript}
                      onChange={(e) => setTranscript(e.target.value)}
                      placeholder={
                        stage === "setup"
                          ? "Start the interview..."
                          : isAISpeaking
                          ? "AI is asking the question..."
                          : isListening
                          ? "Listening... "
                          : "Type your answer..."
                      }
                      disabled={
                        stage !== "questioning" || isAISpeaking || !interviewStarted
                      }
                      sx={{
                        bgcolor: "#1e293b",
                        borderRadius: "0 !important",

                        "& .MuiOutlinedInput-root": {
                          height: "180px",
                          alignItems: "flex-start",
                          borderRadius: "0 !important",
                          color: "#fff",
                          padding: "10px",

                          // ✅ IMPORTANT FIX
                          "& fieldset": {
                            borderColor: "#334155",
                          },
                          "&:hover fieldset": {
                            borderColor: "#475569",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#00e5ff",   // cleaner blue
                            borderWidth: "1px"
                          },
                        },

                        // ✅ THIS FIXES BORDER BREAK
                        "& textarea": {
                          height: "100% !important",
                          overflowY: "auto",
                          paddingRight: "6px",   // prevents scroll overlap
                          boxSizing: "border-box"
                        },

                        "& textarea::placeholder": {
                          color: isAISpeaking
                            ? "rgba(255,255,255,0.45)"
                            : "rgba(255,255,255,0.6)",
                        },

                        "& .MuiInputBase-input.Mui-disabled": {
                          WebkitTextFillColor: "rgba(255,255,255,0.6)",
                        },
                      }}
                    />
                  )}

                  <Box sx={{ mt: 1, flexShrink: 0 }}>
                  {showNextButton && (
                    <Button
                      variant="contained"
                      fullWidth
                      sx={{
                        mt: isCodingActive ? 1 : 2,
                        py: 1.5,
                        borderRadius: "0 !important",
                        textTransform: "none",
                        fontWeight: 600,
                        letterSpacing: "0.3px",
                        background: "linear-gradient(135deg, #14b8a6, #0ea5e9)",
                        boxShadow: "0 6px 20px rgba(0,0,0,0.25)",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          background: "linear-gradient(135deg, #0d9488, #0284c7)",
                        }
                      }}
                      onClick={handleNextQuestion}
                      disabled={loadingNext || stage !== "questioning"}
                    >
                      Next Question
                    </Button>
                  )}
                  </Box>
                  </Box>
                 )}


                </Box>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <ViolationModal
        open={violationModal.open}
        title={violationModal.title}
        message={violationModal.message}
        type={violationModal.type}
        onConfirm={violationModal.onConfirm}
      />

      <Dialog
        open={showIdleModal}
        onClose={() => resetIdleTimer()}
        PaperProps={{
          style: {
            backgroundColor: '#1e293b',
            color: 'white',
            borderRadius: '0 !important',
            padding: '10px',
            minWidth: '320px'
          },
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold', color: 'white' }}>
          Are you still there?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ textAlign: 'center', mb: 2, color: 'white' }}>
            We noticed you've been inactive for a while. Please stay engaged as it may affect evaluation.
          </Typography>
        </DialogContent>
      </Dialog>

      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }

         .interview-scroll {
           overflow-y: scroll !important;

           /* Firefox */
           scrollbar-width: auto;
           scrollbar-color: #94a3b8 #1e293b;
         }

         /* Chrome / Edge / Safari */
         .interview-scroll::-webkit-scrollbar {
           width: 14px;   /* 🔥 thicker like your 2nd image */
         }

         .interview-scroll::-webkit-scrollbar-track {
           background: #1e293b;   /* dark track */
         }

         .interview-scroll::-webkit-scrollbar-thumb {
           background-color: #9ca3af;   /* brighter grey */
           border-radius: 0 !important;
           min-height: 40px;           /* 🔥 FORCE visible thumb */
         }

         .interview-scroll::-webkit-scrollbar-thumb:hover {
           background-color: #cbd5f5;
         }
        `}
      </style>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default AIInterview;
